import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
    Receipt,
    Plus,
    Calendar,
    MapPin,
    DollarSign,
    FileText,
    CheckCircle,
    Clock,
    AlertCircle,
    Briefcase,
    Building,
    User,
    ChevronRight,
    ChevronDown,
    Utensils,
    Moon,
    Coffee,
    BedDouble,
    Car,
    Bus,
    ShoppingBag,
    Trash2,
    Save,
    Send,
    ArrowLeft,
    TrendingUp,
    TrendingDown,
    CreditCard,
    ImagePlus,
    Upload,
    Image,
    X,
    XCircle,
    RefreshCw,
    Eye,
    ThumbsUp,
    ThumbsDown,
    MessageSquare,
    Users,
    FileDown,
    Loader2,
    Ban,
    Search,
    ShieldCheck,
    AlertTriangle,
    Info
} from 'lucide-react';
import { FaMagic } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Modal from './common/Modal';
import ConfirmDialog from './Proyectos/ConfirmDialog';
import toast from 'react-hot-toast';
import axiosInstance from '../utils/axiosConfig';

// Componente de Tarjeta de Resumen (Stats)
// Comidas que le tocan al viaje: una de cada una por dia, salvo el dia de salida,
// que depende de la hora (hay que salir ANTES de la hora tope para alcanzarla).
// Mismo calculo que hace el backend en SolicitudViatico.calcular_comidas, que es
// el que manda; aca solo se replica para mostrarlo mientras se llena el formulario.
const HORA_TOPE_DESAYUNO = '08:00';
const HORA_TOPE_ALMUERZO = '12:00';
// Jornada extra que arranca desde esta hora: solo cena.
const HORA_JORNADA_SOLO_CENA = '17:00';

// Fecha de hoy en 'YYYY-MM-DD' segun el reloj local, para el input de tipo date.
const hoyISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// 'YYYY-MM-DD' -> '14/08/2026', sin pasar por Date para que no corra un dia por
// el huso horario.
const fechaCorta = (iso) => {
    if (!iso) return '';
    const [a, m, d] = `${iso}`.split('-');
    return d && m && a ? `${d}/${m}/${a}` : iso;
};

// Las facturas y los comprobantes pueden ser imagen o PDF.
const esPdf = (ruta) => /\.pdf(\?.*)?$/i.test(ruta || '');
const esArchivoPdf = (archivo) => !!archivo && (archivo.type === 'application/pdf' || /\.pdf$/i.test(archivo.name || ''));
const TilePdf = ({ nombre, className = '' }) => (
    <div className={`flex flex-col items-center justify-center gap-0.5 bg-red-50 border border-red-100 text-red-600 shrink-0 ${className}`}>
        <FileText size={20} />
        <span className="text-[10px] font-bold">PDF</span>
        {nombre && <span className="text-[10px] px-1 truncate max-w-full">{nombre}</span>}
    </div>
);

// Un PDF no se puede mostrar en el visor de imagenes: se abre en una pestaña nueva,
// donde el navegador deja verlo y descargarlo.
const urlArchivo = (ruta) => (!ruta ? null : ruta.startsWith('/api') ? ruta : `/api${ruta}`);
const EnlacePdf = ({ ruta, children, className = '' }) => (
    <a href={urlArchivo(ruta)} target="_blank" rel="noreferrer" title="Abrir PDF" className={`shrink-0 hover:opacity-80 transition-opacity ${className}`}>
        {children}
    </a>
);

// Miniatura de un recibo ya cargado: el PDF se abre en otra pestaña y la imagen en el visor.
const MiniaturaRecibo = ({ ruta, onPreview, className = 'w-16 h-16 rounded-lg', etiqueta }) => {
    if (!ruta) return null;
    const contenido = esPdf(ruta) ? (
        <EnlacePdf ruta={ruta}><TilePdf className={className} /></EnlacePdf>
    ) : (
        <img
            src={urlArchivo(ruta)}
            alt="Recibo"
            className={`${className} object-cover cursor-pointer shrink-0 border border-gray-100`}
            onClick={() => onPreview(urlArchivo(ruta))}
        />
    );
    return (
        <div className="relative shrink-0">
            {contenido}
            {etiqueta && <span className="absolute bottom-0.5 left-0.5 px-1 bg-white/90 rounded text-[9px] font-semibold text-gray-600 pointer-events-none">{etiqueta}</span>}
        </div>
    );
};

// Tipos de factura al legalizar. Las de comida van una por dia del viaje; el resto
// es libre (puede haber varios taxis o compras el mismo dia).
const TIPOS_FACTURA = [
    { id: 'desayuno', label: 'Desayuno', icon: Coffee },
    { id: 'almuerzo', label: 'Almuerzo', icon: Utensils },
    { id: 'cena', label: 'Cena', icon: Moon },
    { id: 'hospedaje', label: 'Hospedaje', icon: BedDouble },
    { id: 'traslado', label: 'Traslado', icon: Car },
    { id: 'otros', label: 'Otros', icon: ShoppingBag },
];

const TIPOS_COMIDA = ['desayuno', 'almuerzo', 'cena'];
const esTipoComida = (tipo) => TIPOS_COMIDA.includes(tipo);

// Cuantas de este tipo de comida se aprobaron (lo que el empleado pidio al crear
// la solicitud), sin depender de que 'selectedSolicitud' este en el scope.
const aprobadasDeSolicitud = (solicitud, tipo) =>
    solicitud?.comidas?.[`${tipo}s`] ?? (solicitud?.plan_comidas || []).filter(d => d[tipo]).length;

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// "Lun 28 sep" a partir de '2026-09-28'.
const partesFecha = (iso) => {
    const [a, m, d] = `${iso}`.split('-').map(Number);
    return { semana: DIAS_SEMANA[new Date(Date.UTC(a, m - 1, d)).getUTCDay()], numero: d, mes: MESES_CORTOS[m - 1] };
};

// Pestañas de "Cargar Facturas": solo las categorias que la solicitud realmente
// pidio (comida en 0, o el gasto opcional sin marcar, no ocupan espacio). La
// tarjeta gerencial es post-pago libre, ahi se dejan todas.
const tabsVisiblesPara = (solicitud) => {
    if (!solicitud) return TIPOS_FACTURA;
    if (solicitud.es_tarjeta_gerencial) return TIPOS_FACTURA;
    return TIPOS_FACTURA.filter(({ id }) => {
        if (esTipoComida(id)) return aprobadasDeSolicitud(solicitud, id) > 0;
        if (id === 'hospedaje') return (solicitud.hospedaje?.costoPorNoche || 0) > 0;
        if (id === 'traslado') return (solicitud.traslado?.monto || 0) > 0;
        if (id === 'otros') return (solicitud.otros?.monto || 0) > 0;
        return true;
    });
};

const formatoBs = (valor) => (valor ?? 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Las tasas de Profit se manejan con 4 decimales, tanto para la variacion como para
// convertir las facturas: se muestran igual que como se calculan.
const formatoTasa = (valor) => (valor ?? 0).toLocaleString('es-VE', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const TOPES_COMIDA = { desayuno: 10, almuerzo: 18, cena: 15 };
const formatoPorcentaje = (valor) => `${valor >= 0 ? '+' : ''}${((valor ?? 0) * 100).toFixed(2)}%`;

// Lineas del anticipo con monto > 0, con la misma cuenta que
// SolicitudViatico.calcular_total en el backend (de ahi sale monto_total).
const desgloseAnticipo = (s) => {
    const lineas = [];
    [
        { key: 'desayunos', tipo: 'desayuno', label: 'Desayunos', icon: Coffee },
        { key: 'almuerzos', tipo: 'almuerzo', label: 'Almuerzos', icon: Utensils },
        { key: 'cenas', tipo: 'cena', label: 'Cenas', icon: Moon },
    ].forEach(({ key, tipo, label, icon }) => {
        const cantidad = s?.comidas?.[key] || 0;
        if (cantidad > 0) {
            lineas.push({ id: key, label, icon, formula: `${cantidad} × $${TOPES_COMIDA[tipo]}`, monto: cantidad * TOPES_COMIDA[tipo] });
        }
    });
    const noches = s?.hospedaje?.noches || 0;
    const costoNoche = s?.hospedaje?.costoPorNoche || 0;
    if (costoNoche > 0 && noches > 0) {
        lineas.push({ id: 'hospedaje', label: 'Hospedaje', icon: BedDouble, formula: `${noches} ${noches === 1 ? 'noche' : 'noches'} × $${costoNoche.toFixed(2)}`, monto: noches * costoNoche });
    }
    if ((s?.traslado?.monto || 0) > 0) {
        lineas.push({ id: 'traslado', label: 'Traslado', icon: Car, detalle: s.traslado.detalle, monto: s.traslado.monto });
    }
    if ((s?.otros?.monto || 0) > 0) {
        lineas.push({ id: 'otros', label: 'Otros', icon: ShoppingBag, detalle: s.otros.detalle, monto: s.otros.monto });
    }
    return { lineas, total: lineas.reduce((acc, l) => acc + l.monto, 0) };
};

// Noches de hospedaje: una por cada noche que duerme fuera. Del 17 al 19 son 2
// (duerme el 17 y el 18; el 19 regresa). Ida y vuelta el mismo dia no lleva.
const calcularNoches = (fechaInicio, fechaFin) => {
    if (!fechaInicio || !fechaFin) return 0;
    const inicio = new Date(`${fechaInicio}T00:00:00Z`);
    const fin = new Date(`${fechaFin}T00:00:00Z`);
    if (isNaN(inicio) || isNaN(fin)) return 0;
    return Math.max(0, Math.round((fin - inicio) / 86400000));
};

const calcularComidas = (fechaInicio, fechaFin, horaSalida) => {
    const vacio = { desayunos: 0, almuerzos: 0, cenas: 0 };
    if (!fechaInicio || !fechaFin) return vacio;

    // Las fechas del input vienen como 'YYYY-MM-DD'; se comparan en UTC para que
    // el huso horario no corra un dia.
    const inicio = new Date(`${fechaInicio}T00:00:00Z`);
    const fin = new Date(`${fechaFin}T00:00:00Z`);
    if (isNaN(inicio) || isNaN(fin)) return vacio;

    const dias = Math.round((fin - inicio) / 86400000) + 1;
    if (dias < 1) return vacio;

    // Sin hora (solicitudes viejas) se cuenta el dia completo.
    const llevaDesayuno = !horaSalida || horaSalida < HORA_TOPE_DESAYUNO;
    const llevaAlmuerzo = !horaSalida || horaSalida < HORA_TOPE_ALMUERZO;

    return {
        desayunos: (dias - 1) + (llevaDesayuno ? 1 : 0),
        almuerzos: (dias - 1) + (llevaAlmuerzo ? 1 : 0),
        cenas: dias,
    };
};

// Gasto opcional del anticipo: solo ocupa una fila hasta que se marca el check.
const GastoOpcional = ({ activo, onToggle, icon: Icon, iconColor, titulo, total, children }) => (
    <div className={`rounded-2xl border transition-colors ${activo ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 hover:bg-gray-50/60'}`}>
        <label className="flex items-center justify-between gap-3 px-5 py-4 cursor-pointer select-none">
            <span className="flex items-center gap-3">
                <input
                    type="checkbox"
                    checked={activo}
                    onChange={e => onToggle(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 accent-gray-900 cursor-pointer"
                />
                <span className="font-medium text-gray-900 flex items-center gap-2">
                    <Icon size={18} className={iconColor} /> {titulo}
                </span>
            </span>
            {activo
                ? <span className="text-xl font-bold text-gray-900">${total.toFixed(2)}</span>
                : <span className="text-xs text-gray-400">Marca si lo necesitas</span>}
        </label>
        {activo && <div className="px-5 pb-5">{children}</div>}
    </div>
);

const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
    <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm flex items-center gap-4">
        <div className={`p-4 rounded-2xl ${color}`}>
            <Icon size={24} className="text-white" />
        </div>
        <div>
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
            {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
    </div>
);

// Badge de estado
const EstadoBadge = ({ estado, legalizacion }) => {
    if (estado === 'legalizado' && legalizacion === 'pendiente_cxp') {
        return (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider border bg-amber-50 text-amber-700 border-amber-200">
                <Clock size={12} />
                Pendiente CxP
            </span>
        );
    }
    const config = {
        pendiente: { label: 'Pendiente por aprobar', bg: 'bg-amber-50 text-amber-600 border-amber-100', icon: Clock },
        aprobado: { label: 'Aprobado', bg: 'bg-blue-50 text-blue-600 border-blue-100', icon: CheckCircle },
        rechazado: { label: 'Rechazado', bg: 'bg-red-50 text-red-600 border-red-100', icon: XCircle },
        pagado: { label: 'Pagado', bg: 'bg-indigo-50 text-indigo-600 border-indigo-100', icon: CreditCard },
        legalizado: { label: 'Legalizado', bg: 'bg-emerald-50 text-emerald-600 border-emerald-100', icon: CheckCircle },
        anulado: { label: 'Anulado', bg: 'bg-slate-100 text-slate-500 border-slate-200', icon: Ban }
    };
    const c = estado === 'legalizado' && legalizacion === 'cerrado'
        ? { ...config.legalizado, label: 'Legalizado · cerrado' }
        : (config[estado] || config.pendiente);
    const IconComp = c.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider border ${c.bg}`}>
            <IconComp size={12} />
            {c.label}
        </span>
    );
};

// Un pago del reintegro (del empleado a CxP) en la tarjeta de gestion. Mientras la
// solicitud esta en Pagado o por confirmar, CxP puede corregir el monto si la IA lo
// leyo mal, anular el pago si no es valido o deshacer una anulacion.
const AJUSTES_REINTEGRO = {
    corregir: {
        boton: 'Guardar monto',
        motivo: 'Motivo (ej. la IA leyó mal el monto)',
        aviso: 'Queda el monto que ves en el comprobante; el de la IA se guarda en el historial.',
    },
    anular: {
        boton: 'Anular pago',
        motivo: 'Motivo (ej. la transferencia fue rechazada)',
        aviso: 'El pago deja de contar y el empleado debe reintegrar ese monto. Este comprobante no se puede volver a subir.',
    },
    restaurar: {
        boton: 'Restaurar pago',
        motivo: 'Motivo (ej. se anuló por error)',
        aviso: 'El pago vuelve a contar para el reintegro.',
    },
};

const PagoReintegro = ({ pago: r, puedeAjustar, src, onPreview, onAjustar }) => {
    const [modo, setModo] = useState(null);
    const [monto, setMonto] = useState('');
    const [motivo, setMotivo] = useState('');
    const [enviando, setEnviando] = useState(false);

    const abrir = (m) => { setModo(m); setMotivo(''); setMonto(m === 'corregir' ? String(r.monto_bs ?? '') : ''); };
    const cerrar = () => { setModo(null); setMotivo(''); setMonto(''); };
    const montoValido = parseFloat(monto) > 0;
    const listo = !!motivo.trim() && (modo !== 'corregir' || montoValido);

    const guardar = async () => {
        if (!listo || enviando) return;
        setEnviando(true);
        const ok = await onAjustar({
            sha256: r.sha256,
            accion: modo,
            monto_bs: modo === 'corregir' ? parseFloat(monto) : undefined,
            motivo: motivo.trim(),
        });
        setEnviando(false);
        if (ok) cerrar();
    };

    return (
        <div className="p-3 bg-white rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
                <div className={`flex items-center gap-3 flex-1 min-w-0 ${r.anulado ? 'opacity-60' : ''}`}>
                    {r.archivo && (esPdf(r.archivo) ? (
                        <a href={src} target="_blank" rel="noreferrer">
                            <TilePdf className="w-14 h-14 rounded-lg" />
                        </a>
                    ) : (
                        <img
                            src={src}
                            alt="Comprobante del reintegro"
                            className="w-14 h-14 rounded-lg object-cover cursor-pointer shrink-0 border border-gray-100"
                            onClick={() => onPreview(src)}
                        />
                    ))}
                    <div className="flex-1 min-w-0">
                        <p className={`font-semibold text-gray-900 ${r.anulado ? 'line-through' : ''}`}>Bs. {formatoBs(r.monto_bs || 0)}</p>
                        <p className="text-xs text-gray-500">
                            {r.fecha ? new Date(r.fecha).toLocaleDateString('es-VE') : ''}
                            {r.referencia ? ` · Ref. ${r.referencia}` : ' · referencia no leída'}
                        </p>
                        {r.monto_leido_ia != null && (
                            <p className="text-xs text-gray-500">Corregido por CxP · la IA leyó Bs. {formatoBs(r.monto_leido_ia)}</p>
                        )}
                        {r.anulado && (
                            <p className="text-xs text-red-500">Anulado por CxP{r.motivo_anulacion ? `: ${r.motivo_anulacion}` : ''}</p>
                        )}
                    </div>
                </div>
                {puedeAjustar && r.sha256 && !modo && (
                    <div className="flex items-center gap-3 shrink-0 text-xs font-medium">
                        {r.anulado ? (
                            <button onClick={() => abrir('restaurar')} className="text-blue-600 hover:text-blue-800">Restaurar</button>
                        ) : (
                            <>
                                <button onClick={() => abrir('corregir')} className="text-blue-600 hover:text-blue-800">Corregir monto</button>
                                <button onClick={() => abrir('anular')} className="text-red-600 hover:text-red-800">Anular</button>
                            </>
                        )}
                    </div>
                )}
            </div>
            {modo && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    <p className="text-xs text-gray-500">{AJUSTES_REINTEGRO[modo].aviso}</p>
                    {modo === 'corregir' && (
                        <label className="block text-xs text-gray-600">
                            Monto que muestra el comprobante (Bs.)
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={monto}
                                onChange={e => setMonto(e.target.value)}
                                className="mt-1 w-full p-2 bg-white border border-gray-200 rounded-lg outline-none text-sm focus:border-blue-400"
                            />
                        </label>
                    )}
                    <input
                        type="text"
                        value={motivo}
                        onChange={e => setMotivo(e.target.value)}
                        placeholder={AJUSTES_REINTEGRO[modo].motivo}
                        className="w-full p-2 bg-white border border-gray-200 rounded-lg outline-none text-sm focus:border-blue-400"
                    />
                    <div className="flex justify-end gap-2">
                        <button onClick={cerrar} className="px-3 py-1.5 text-gray-600 text-xs font-medium hover:bg-gray-50 rounded-lg transition-colors">
                            Cancelar
                        </button>
                        <button
                            disabled={!listo || enviando}
                            onClick={guardar}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${modo === 'anular' ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-900 hover:bg-gray-800'}`}
                        >
                            {enviando && <RefreshCw size={12} className="animate-spin" />}
                            {AJUSTES_REINTEGRO[modo].boton}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Card expandible para gestión CxP (muestra comprobantes/facturas)
const GestionCard = ({ solicitud: s, onPagar, onAnular, onAprobarCxp, onDownloadPdf, onConfirmarLegalizacion, onDevolverLegalizacion, onAjustarReintegro }) => {
    const [expanded, setExpanded] = useState(false);
    // Confirmar la legalizacion se hace con el detalle abierto: CxP revisa facturas y
    // pagos del reintegro en la misma tarjeta antes de cerrar.
    const [confirmando, setConfirmando] = useState(false);
    const [notaConfirmacion, setNotaConfirmacion] = useState('');
    const [enviandoConfirmacion, setEnviandoConfirmacion] = useState(false);
    const pendienteCxp = s.estado === 'legalizado' && s.legalizacion_estado === 'pendiente_cxp';
    const reintegros = s.reintegros || [];
    const [previewImg, setPreviewImg] = useState(null);
    // Filtro por tipo de factura en el detalle (todos, desayuno, almuerzo, ...)
    const [tabGestion, setTabGestion] = useState('todos');
    const comprobantes = s.comprobantes || [];
    const totalLegalizado = (s.monto_legalizado || 0);
    const esTarjeta = s.es_tarjeta_gerencial;
    const puedeAnular = ['pendiente', 'aprobado', 'pagado'].includes(s.estado);
    // CxP aprueba en lugar del supervisor solo si este no puede (vacaciones, reposo...)
    const puedeAprobarCxp = s.estado === 'pendiente';

    // Las facturas se cargan en Bs y el backend las convierte con la tasa del dia de
    // cada una; el saldo se cuadra en dolares porque el anticipo se aprobo en dolares.
    const totalLegalizadoUsd = comprobantes.reduce((acc, c) => {
        const reconocido = c.monto_usd_reconocido != null ? c.monto_usd_reconocido : c.monto_usd;
        return acc + parseFloat(reconocido || 0);
    }, 0);
    const saldoUsd = (s.monto_total || 0) - totalLegalizadoUsd;
    // En bolivares: lo reconocido de cada factura a su tasa (lo que se pasa del tope no
    // cuenta), para que Abonado - Legalizado = Saldo se vea directo, sin dolares.
    const bsFactura = (c, usdParte, sinConversion) => {
        const bs = parseFloat(c.monto || 0);
        const usd = parseFloat(c.monto_usd || 0);
        return usd > 0 ? bs * (parseFloat(usdParte || 0) / usd) : sinConversion;
    };
    const legalizadoBs = comprobantes.reduce((acc, c) => acc + bsFactura(
        c, c.monto_usd_reconocido != null ? c.monto_usd_reconocido : c.monto_usd, parseFloat(c.monto || 0)
    ), 0);
    const excedidoBs = comprobantes.reduce((acc, c) => acc + bsFactura(c, c.excedente_usd, 0), 0);
    // La direccion del saldo sale del saldo en Bs cuando ya hay abono.
    const saldoDir = s.saldo_reintegro_bs != null ? s.saldo_reintegro_bs : saldoUsd;

    // Un saldo positivo es plata del anticipo que sobró y el empleado debe reintegrar.
    // Si ya lo pagó se decide con los montos (lo reintegrado contra el saldo), no con
    // la ultima lectura de la IA: un pago parcial seguido de otro deja la deuda saldada.
    const historialSolicitud = s.historial || [];
    const lecturaReintegro = [...historialSolicitud].reverse().find(h => h.accion === 'reintegro_verificado');
    const reintegroOk = lecturaReintegro?.veredicto === 'coincide';
    // Facturas que la IA no pudo confirmar (fecha sin leer o con avisos de RIF, fecha
    // o tipo de documento): se cargaron igual y CxP las revisa.
    const facturasConAvisos = comprobantes.filter(c => c.fecha_verificada === false || (c.avisos_ia || []).length > 0).length;
    const pendienteReintegro = s.pendiente_reintegro_bs;
    let saldoEtiqueta = 'cuadrado';
    let saldoColor = 'text-gray-900';
    if (saldoDir > 1 || (s.saldo_reintegro_bs == null && saldoUsd > 0)) {
        if (pendienteReintegro != null && pendienteReintegro <= 1) {
            saldoEtiqueta = 'reintegro pagado';
            saldoColor = 'text-emerald-600';
        } else if (s.estado !== 'legalizado') {
            saldoEtiqueta = (s.reintegrado_bs || 0) > 0
                ? `reintegro parcial: faltan Bs. ${formatoBs(pendienteReintegro)}`
                : 'por legalizar';
            saldoColor = 'text-red-600';
        } else if (reintegros.length === 0 && s.comprobante_pago) {
            // Legalizadas antes de que la IA registrara los pagos del reintegro.
            saldoEtiqueta = 'comprobante del reintegro cargado';
            saldoColor = 'text-amber-600';
        } else {
            saldoEtiqueta = 'por reintegrar';
            saldoColor = 'text-red-600';
        }
    } else if (saldoDir < 0) {
        saldoEtiqueta = 'a favor del empleado';
        saldoColor = 'text-amber-600';
    }

    const porTipo = useMemo(() => {
        const acumulado = { total: { cantidad: comprobantes.length, bs: 0, usd: 0, exceso: 0 } };
        TIPOS_FACTURA.forEach(({ id }) => { acumulado[id] = { cantidad: 0, bs: 0, usd: 0, exceso: 0 }; });
        comprobantes.forEach(c => {
            const tipo = acumulado[c.tipo] ? c.tipo : 'otros';
            const bs = parseFloat(c.monto || 0);
            const usd = parseFloat(c.monto_usd_reconocido != null ? c.monto_usd_reconocido : c.monto_usd || 0);
            const exceso = parseFloat(c.excedente_usd || 0);
            acumulado[tipo].cantidad += 1;
            acumulado[tipo].bs += bs;
            acumulado[tipo].usd += usd;
            acumulado[tipo].exceso += exceso;
            acumulado.total.bs += bs;
            acumulado.total.usd += usd;
            acumulado.total.exceso += exceso;
        });
        return acumulado;
    }, [comprobantes]);

    const comprobantesFiltrados = tabGestion === 'todos'
        ? comprobantes
        : comprobantes.filter(c => (c.tipo || 'otros') === tabGestion);

    const resolveImg = (path) => {
        if (!path) return null;
        return path.startsWith('/api') ? path : `/api${path}`;
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-100 hover:shadow-md transition-all overflow-hidden">
            <div className="p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${s.estado === 'anulado' ? 'bg-slate-100 text-slate-500' : s.estado === 'pendiente' ? 'bg-amber-100 text-amber-600' : s.estado === 'aprobado' ? 'bg-blue-100 text-blue-600' : s.estado === 'pagado' ? 'bg-indigo-100 text-indigo-600' : 'bg-emerald-100 text-emerald-600'}`}>
                            {s.estado === 'anulado' ? <Ban size={24} /> : s.estado === 'pendiente' ? <Clock size={24} /> : s.estado === 'aprobado' ? <DollarSign size={24} /> : s.estado === 'pagado' ? <CreditCard size={24} /> : <CheckCircle size={24} />}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-bold text-gray-900">{s.empleado_nombre}</h3>
                                <span className="text-sm text-gray-400">{s.codigo}</span>
                                {esTarjeta && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#fbf1d3] text-[#8a6d1a] border border-[#e6cf7a]">
                                        <CreditCard size={11} /> Tarjeta gerencial
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-600 mt-0.5">{s.motivo}</p>
                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 flex-wrap">
                                <span className="flex items-center gap-1"><MapPin size={14} /> {s.destino}</span>
                                <span className="flex items-center gap-1"><Calendar size={14} /> {s.fecha_inicio} — {s.fecha_fin}</span>
                                {s.aprobador_nombre && (
                                    s.aprobado_por_cxp_nombre ? (
                                        <span className="flex items-center gap-1"><ShieldCheck size={14} className="text-emerald-500" /> Aprobado por CxP · {s.aprobado_por_cxp_nombre}{s.fecha_aprobacion ? ` el ${new Date(s.fecha_aprobacion).toLocaleDateString('es-VE')}` : ''}, en lugar de {s.aprobador_nombre}</span>
                                    ) : s.fecha_aprobacion ? (
                                        <span className="flex items-center gap-1"><CheckCircle size={14} className="text-emerald-500" /> Aprobado por {s.aprobador_nombre} el {new Date(s.fecha_aprobacion).toLocaleDateString('es-VE')}</span>
                                    ) : s.estado === 'pendiente' ? (
                                        <span className="flex items-center gap-1"><Clock size={14} className="text-amber-500" /> Esperando aprobación de {s.aprobador_nombre}</span>
                                    ) : (
                                        <span className="flex items-center gap-1"><CheckCircle size={14} className="text-emerald-500" /> Aprobado por {s.aprobador_nombre}</span>
                                    )
                                )}
                                {s.fecha_pago && (
                                    <span className="flex items-center gap-1"><CreditCard size={14} className="text-blue-500" /> Pagado el {new Date(s.fecha_pago).toLocaleDateString('es-VE')}</span>
                                )}
                            </div>
                            {s.motivo_aprobacion_cxp && (
                                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                    <Info size={12} className="text-gray-400" /> Motivo: {s.motivo_aprobacion_cxp}
                                </p>
                            )}
                            {s.estado === 'anulado' && s.motivo_anulacion && (
                                <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                                    <Ban size={12} /> Anulado: {s.motivo_anulacion}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-right">
                            {esTarjeta ? (
                                <>
                                    <p className="text-sm text-gray-500">Facturas</p>
                                    <p className="text-lg font-bold text-gray-900">Bs. {totalLegalizado.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</p>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-gray-500">Anticipo ($ ref.)</p>
                                    <p className="text-lg font-bold text-gray-900">${s.monto_total?.toFixed(2)}</p>
                                    {s.monto_bs > 0 && (
                                        <p className="text-sm font-semibold text-blue-600">Bs. {s.monto_bs?.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</p>
                                    )}
                                    {s.tasa_aplicada > 0 && (
                                        <p className="text-xs text-gray-400">
                                            Tasa {formatoTasa(s.tasa_aplicada)}
                                            {s.fecha_pago_efectiva ? ` · ${fechaCorta(s.fecha_pago_efectiva)}` : ''}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                        {s.estado === 'aprobado' && !esTarjeta && (
                            <button
                                onClick={onPagar}
                                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm"
                            >
                                <CreditCard size={16} /> Registrar Pago
                            </button>
                        )}
                        {puedeAprobarCxp && onAprobarCxp && (
                            <button
                                onClick={onAprobarCxp}
                                title={`Aprobar en lugar de ${s.aprobador_nombre || 'el supervisor'}`}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white text-amber-700 border border-amber-200 rounded-xl hover:bg-amber-50 transition-colors font-medium text-sm"
                            >
                                <ShieldCheck size={16} /> Aprobar por CxP
                            </button>
                        )}
                        {pendienteCxp && onConfirmarLegalizacion && (
                            <button
                                onClick={() => { setExpanded(true); setConfirmando(true); }}
                                className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium text-sm"
                            >
                                <ShieldCheck size={16} /> Confirmar y cerrar
                            </button>
                        )}
                        {pendienteCxp && onDevolverLegalizacion && (
                            <button
                                onClick={onDevolverLegalizacion}
                                title="Devolver al empleado para que corrija"
                                className="flex items-center gap-2 px-4 py-2.5 bg-white text-amber-700 border border-amber-200 rounded-xl hover:bg-amber-50 transition-colors font-medium text-sm"
                            >
                                <RefreshCw size={16} /> Devolver
                            </button>
                        )}
                        {puedeAnular && onAnular && (
                            <button
                                onClick={onAnular}
                                title="Anular solicitud"
                                className="flex items-center gap-2 px-4 py-2.5 bg-white text-red-500 border border-red-200 rounded-xl hover:bg-red-50 transition-colors font-medium text-sm"
                            >
                                <Ban size={16} /> Anular
                            </button>
                        )}
                        <button
                            onClick={onDownloadPdf}
                            title="Descargar PDF"
                            className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm"
                        >
                            <FileDown size={16} />
                        </button>
                        <EstadoBadge estado={s.estado} legalizacion={s.legalizacion_estado} />
                    </div>
                </div>

                {s.estado === 'legalizado' && s.legalizacion_estado === 'cerrado' && s.legalizacion_confirmada_por && (
                    <p className="text-xs text-emerald-700 mt-3 flex items-center gap-1">
                        <ShieldCheck size={12} />
                        Verificado y cerrado por {s.legalizacion_confirmada_por}
                        {s.legalizacion_confirmada_fecha ? ` el ${new Date(s.legalizacion_confirmada_fecha).toLocaleDateString('es-VE')}` : ''}
                        {s.legalizacion_confirmada_nota ? ` · ${s.legalizacion_confirmada_nota}` : ''}
                    </p>
                )}

                {(comprobantes.length > 0 || s.comprobante_pago || reintegros.length > 0) && (
                    <button
                        onClick={() => { setExpanded(!expanded); if (expanded) setConfirmando(false); }}
                        className="mt-4 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                        <Eye size={14} />
                        {expanded ? 'Ocultar' : `Ver ${comprobantes.length > 0 ? `${comprobantes.length} comprobante(s)` : 'comprobante de pago'}`}
                    </button>
                )}
            </div>

            {(expanded || confirmando) && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="border-t border-gray-100 bg-gray-50 p-6"
                >
                    {comprobantes.length > 0 && (
                        <>
                            <div className="flex items-baseline justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-700">Facturas legalizadas</h4>
                                <span className="text-xs text-gray-400">
                                    {comprobantes.length} {comprobantes.length === 1 ? 'factura' : 'facturas'}
                                </span>
                            </div>

                            {/* Resumen por tipo: cada cuadro filtra la lista de abajo */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
                                <button
                                    type="button"
                                    onClick={() => setTabGestion('todos')}
                                    className={`p-3 rounded-xl border text-left transition-colors ${tabGestion === 'todos'
                                        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                        : 'border-gray-100 bg-white hover:border-blue-200'}`}
                                >
                                    <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Todas</p>
                                    <p className="text-base font-bold text-gray-900 leading-tight mt-0.5">
                                        $ {porTipo.total.usd.toFixed(2)}
                                    </p>
                                    <p className="text-[11px] text-gray-400">Bs {formatoBs(porTipo.total.bs)}</p>
                                </button>

                                {TIPOS_FACTURA.map(({ id, label, icon: Icon }) => {
                                    const datos = porTipo[id];
                                    const activo = tabGestion === id;
                                    const vacio = datos.cantidad === 0;
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            disabled={vacio}
                                            onClick={() => setTabGestion(id)}
                                            className={`p-3 rounded-xl border text-left transition-colors ${activo
                                                ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                                : vacio
                                                    ? 'border-gray-100 bg-white/50 cursor-not-allowed'
                                                    : 'border-gray-100 bg-white hover:border-blue-200'}`}
                                        >
                                            <p className={`text-[11px] font-medium uppercase tracking-wide flex items-center gap-1 ${vacio ? 'text-gray-300' : 'text-gray-500'}`}>
                                                <Icon size={11} /> {label}
                                            </p>
                                            <p className={`text-base font-bold leading-tight mt-0.5 ${vacio ? 'text-gray-300' : 'text-gray-900'}`}>
                                                $ {datos.usd.toFixed(2)}
                                            </p>
                                            <p className={`text-[11px] ${vacio ? 'text-gray-300' : 'text-gray-400'}`}>
                                                {vacio ? 'Sin facturas' : `${datos.cantidad} · Bs ${formatoBs(datos.bs)}`}
                                            </p>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="space-y-2 mb-4">
                                {comprobantesFiltrados.map(c => {
                                    const rifOk = !!c.rif && `${c.rif}`.replace(/\D/g, '').startsWith('30622535');
                                    return (
                                        <div key={c.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                                            <div className="flex gap-3 p-3">
                                                {c.imagen ? (
                                                    esPdf(c.imagen) ? (
                                                        <EnlacePdf ruta={c.imagen}>
                                                            <TilePdf className="w-16 h-16 rounded-lg" />
                                                        </EnlacePdf>
                                                    ) : (
                                                        <img
                                                            src={resolveImg(c.imagen)}
                                                            alt={c.concepto}
                                                            className="w-16 h-16 rounded-lg object-cover cursor-pointer shrink-0 border border-gray-100"
                                                            onClick={() => setPreviewImg(resolveImg(c.imagen))}
                                                        />
                                                    )
                                                ) : (
                                                    <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                                                        <Receipt size={20} />
                                                    </div>
                                                )}
                                                {c.imagen_2 && (
                                                    <MiniaturaRecibo ruta={c.imagen_2} onPreview={setPreviewImg} etiqueta="2º" />
                                                )}

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0">
                                                                    {TIPOS_FACTURA.find(t => t.id === (c.tipo || 'otros'))?.label || 'Otros'}
                                                                </span>
                                                                <p className="font-medium text-gray-900 text-sm truncate">{c.concepto}</p>
                                                            </div>
                                                            {/* Datos fiscales verificados: CxP necesita ver que la
                                                                factura esta a nombre de la empresa. */}
                                                            <div className="flex items-center gap-1.5 mt-1.5">
                                                                {rifOk ? (
                                                                    <CheckCircle size={13} className="text-emerald-500 shrink-0" />
                                                                ) : (
                                                                    <AlertCircle size={13} className="text-amber-500 shrink-0" />
                                                                )}
                                                                <span className={`text-xs truncate ${rifOk ? 'text-emerald-700' : 'text-amber-700'}`}>
                                                                    {c.razon_social || 'Sin razon social'}
                                                                    {c.rif ? ` · ${c.rif}` : ''}
                                                                    {!c.rif && !c.razon_social && !(c.avisos_ia || []).length ? ' (cargada antes de la verificacion)' : ''}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div className="text-right shrink-0">
                                                            <span className="font-bold text-gray-900 block leading-tight text-lg">
                                                                $ {parseFloat(c.monto_usd_reconocido != null ? c.monto_usd_reconocido : c.monto_usd || 0).toFixed(2)}
                                                            </span>
                                                            <span className="text-xs text-gray-400">Bs {formatoBs(parseFloat(c.monto))}</span>
                                                            {c.excedente_usd > 0 && (
                                                                <span className="block text-xs text-amber-600 font-medium">
                                                                    +${parseFloat(c.excedente_usd).toFixed(2)} excedido
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Como se convirtio: fecha del recibo y tasa aplicada */}
                                                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                                                        <span className="flex items-center gap-1">
                                                            <Calendar size={12} className="text-gray-400" />
                                                            {c.fecha ? fechaCorta(c.fecha) : 'Sin fecha'}
                                                        </span>
                                                        {c.fecha_verificada === false && (
                                                            <span className="text-amber-600">fecha no verificada por IA</span>
                                                        )}
                                                        {c.tasa ? (
                                                            <span className="text-gray-400">
                                                                tasa {formatoTasa(c.tasa)}
                                                                {c.fecha_tasa && c.fecha_tasa !== c.fecha ? ` del ${fechaCorta(c.fecha_tasa)}` : ''}
                                                            </span>
                                                        ) : null}
                                                        {c.tope_usd ? (
                                                            <span className="text-gray-400">tope ${c.tope_usd}</span>
                                                        ) : null}
                                                    </div>

                                                    {/* Lo que la IA leyo y no cuadra: no freno la carga, CxP lo revisa */}
                                                    {(c.avisos_ia || []).length > 0 && (
                                                        <div className="mt-2 space-y-1">
                                                            {c.avisos_ia.map((aviso, i) => (
                                                                <p key={i} className="flex items-start gap-1.5 text-xs text-amber-700">
                                                                    <AlertCircle size={12} className="shrink-0 mt-0.5" />
                                                                    <span>{aviso}</span>
                                                                </p>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* El detalle que leyo la IA va aparte: es largo y desordenaba la fila */}
                                            {c.detalle && (
                                                <details className="border-t border-gray-100 group">
                                                    <summary className="px-3 py-2 text-xs text-gray-500 cursor-pointer hover:bg-gray-50 list-none flex items-center gap-1.5">
                                                        <ChevronDown size={13} className="transition-transform group-open:rotate-180" />
                                                        Detalle de la factura
                                                    </summary>
                                                    <p className="px-3 pb-3 text-xs text-gray-500 leading-relaxed">{c.detalle}</p>
                                                </details>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-white rounded-xl border border-gray-100">
                                <div>
                                    <p className="text-xs text-gray-500">Anticipo aprobado</p>
                                    <p className="font-bold text-gray-900">$ {(s.monto_total || 0).toFixed(2)}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Abonado</p>
                                    <p className="font-bold text-blue-600">Bs. {formatoBs(s.monto_bs)}</p>
                                    {s.tasa_aplicada ? (
                                        <p className="text-[11px] text-gray-400">tasa {formatoTasa(s.tasa_aplicada)}</p>
                                    ) : null}
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Legalizado</p>
                                    <p className="font-bold text-emerald-600">Bs. {formatoBs(legalizadoBs)}</p>
                                    {excedidoBs > 0 && (
                                        <p className="text-[11px] text-amber-600 font-medium">
                                            +Bs. {formatoBs(excedidoBs)} excedido (no reconocido)
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Saldo</p>
                                    <p className={`font-bold ${saldoColor}`}>
                                        {s.saldo_reintegro_bs != null
                                            ? `Bs. ${formatoBs(Math.abs(s.saldo_reintegro_bs))}`
                                            : `$ ${Math.abs(saldoUsd).toFixed(2)}`}
                                    </p>
                                    <p className={`text-[11px] ${saldoColor === 'text-gray-900' ? 'text-gray-400' : saldoColor}`}>
                                        {saldoEtiqueta}
                                    </p>
                                </div>
                            </div>
                        </>
                    )}

                    {reintegros.length > 0 && (
                        <div className="mt-4">
                            <div className="flex items-baseline justify-between mb-3">
                                <h4 className="text-sm font-semibold text-gray-700">Pagos del reintegro (del empleado a CxP)</h4>
                                <span className="text-xs text-gray-500">
                                    Registrado Bs. {formatoBs(s.reintegrado_bs || 0)}
                                    {s.saldo_reintegro_bs != null ? ` de Bs. ${formatoBs(s.saldo_reintegro_bs)}` : ''}
                                </span>
                            </div>
                            <div className="space-y-2">
                                {reintegros.map((r, i) => (
                                    <PagoReintegro
                                        key={r.sha256 || i}
                                        pago={r}
                                        puedeAjustar={s.estado === 'pagado' || pendienteCxp}
                                        src={resolveImg(r.archivo)}
                                        onPreview={setPreviewImg}
                                        onAjustar={onAjustarReintegro}
                                    />
                                ))}
                            </div>
                            {lecturaReintegro && (
                                <p className={`text-xs mt-2 ${reintegroOk ? 'text-emerald-700' : 'text-amber-700'}`}>
                                    Última lectura con IA: {lecturaReintegro.detalle}
                                </p>
                            )}
                        </div>
                    )}

                    {s.comprobante_pago && reintegros.length === 0 && (
                        <div className="mt-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Comprobante de pago</h4>
                            {esPdf(s.comprobante_pago) ? (
                                <EnlacePdf ruta={s.comprobante_pago} className="inline-block">
                                    <TilePdf nombre="Comprobante" className="w-40 h-24 rounded-xl" />
                                </EnlacePdf>
                            ) : (
                                <img
                                    src={resolveImg(s.comprobante_pago)}
                                    alt="Comprobante"
                                    className="max-w-xs rounded-xl border border-gray-100 cursor-pointer"
                                    onClick={() => setPreviewImg(resolveImg(s.comprobante_pago))}
                                />
                            )}
                        </div>
                    )}

                    {confirmando && pendienteCxp && (
                        <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                            <p className="text-sm font-semibold text-emerald-800">¿Revisaste facturas, montos y pagos del reintegro?</p>
                            <p className="text-xs text-emerald-700 mt-1">
                                Al confirmar, la legalización queda verificada y cerrada por CxP y se le avisa al empleado.
                                {facturasConAvisos > 0 && ` Ojo: ${facturasConAvisos} factura(s) con avisos de la IA.`}
                            </p>
                            <input
                                type="text"
                                value={notaConfirmacion}
                                onChange={e => setNotaConfirmacion(e.target.value)}
                                placeholder="Nota opcional"
                                className="w-full mt-3 p-2.5 bg-white border border-emerald-200 rounded-lg outline-none text-sm"
                            />
                            <div className="flex justify-end gap-2 mt-3">
                                <button
                                    onClick={() => { setConfirmando(false); setNotaConfirmacion(''); }}
                                    className="px-4 py-2 text-gray-600 text-sm font-medium hover:bg-white rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    disabled={enviandoConfirmacion}
                                    onClick={async () => {
                                        setEnviandoConfirmacion(true);
                                        const ok = await onConfirmarLegalizacion(notaConfirmacion.trim());
                                        setEnviandoConfirmacion(false);
                                        if (ok) { setConfirmando(false); setNotaConfirmacion(''); }
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                >
                                    {enviandoConfirmacion ? <RefreshCw size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                                    Confirmar y cerrar
                                </button>
                            </div>
                        </div>
                    )}
                </motion.div>
            )}

            {/* Portal: el contenedor animado deja un transform y "fixed" dejaria de cubrir la pantalla. */}
            {previewImg && createPortal((
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4 cursor-zoom-out"
                    onClick={() => setPreviewImg(null)}
                >
                    <img src={previewImg} alt="Comprobante" className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl" />
                </div>
            ), document.body)}
        </div>
    );
};

// Componente principal
const ViaticosView = () => {
    const { user } = useAuth();
    // El backend decide quien gestiona (rol cxp/admin, area de Cuentas por Pagar u
    // observador especial) y lo manda en el usuario. El fallback cubre sesiones
    // viejas cuyo token se guardo antes de que existiera el campo.
    const canManageViaticos = user?.ve_gestion_viaticos
        ?? (user?.role === 'cxp' || ['mtravieso', 'aruizc'].includes(user?.username));
    const getInitialViewMode = () => {
        const tab = new URLSearchParams(window.location.search).get('tab');
        if (tab === 'gestion' && canManageViaticos) return 'gestion';
        return 'list';
    };
    const [viewMode, setViewMode] = useState(getInitialViewMode); // 'list', 'create', 'legalize', 'detail', 'edit', 'gestion'
    const [scrollToApprovals, setScrollToApprovals] = useState(() => {
        return new URLSearchParams(window.location.search).get('tab') === 'aprobar';
    });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Estado gestión CxP
    const [gestionSolicitudes, setGestionSolicitudes] = useState([]);
    const [gestionLoading, setGestionLoading] = useState(false);
    const [gestionFiltro, setGestionFiltro] = useState('aprobado');
    // Dentro de Legalizados: las que CxP aun no confirma y las ya cerradas.
    const [legalizadosVista, setLegalizadosVista] = useState('pendientes');
    const [pendientesConfirmacion, setPendientesConfirmacion] = useState(0);
    const [devolverModal, setDevolverModal] = useState(null);
    const [motivoDevolucion, setMotivoDevolucion] = useState('');
    const [reintegrosAnular, setReintegrosAnular] = useState([]);
    const [pagoModal, setPagoModal] = useState(null);
    const [capturaPago, setCapturaPago] = useState(null);
    const [capturaPagoPreview, setCapturaPagoPreview] = useState(null);
    // Visor de la imagen que se esta por subir (factura, comprobante de reintegro o
    // captura de pago), antes de enviarla al servidor.
    const [previewImg, setPreviewImg] = useState(null);
    // Factura ya cargada que se esta viendo: montos y tasa de su conversion bajo la imagen.
    const [previewFactura, setPreviewFactura] = useState(null);
    // Confirmacion con el dialogo del sitio en vez de window.confirm: { titulo, mensaje, accion }
    const [confirmacion, setConfirmacion] = useState(null);
    // CxP elige la fecha del pago; la tasa y el monto en Bs los calcula el backend.
    const [fechaPagoInput, setFechaPagoInput] = useState('');
    const [proyeccionTasa, setProyeccionTasa] = useState(null);
    const [proyeccionLoading, setProyeccionLoading] = useState(false);
    const [proyeccionError, setProyeccionError] = useState('');
    const [verDetalleTasa, setVerDetalleTasa] = useState(false);

    // Anulación por CxP
    const [anularModal, setAnularModal] = useState(null);
    const [motivoAnulacion, setMotivoAnulacion] = useState('');
    const [aprobarCxpModal, setAprobarCxpModal] = useState(null);
    const [motivoAprobacionCxp, setMotivoAprobacionCxp] = useState('');

    // Tarjeta gerencial
    const [tieneTarjetaGerencial, setTieneTarjetaGerencial] = useState(false);
    const [modoTarjeta, setModoTarjeta] = useState(false); // modo del formulario de creación
    const [showTarjetasPanel, setShowTarjetasPanel] = useState(false);
    const [tarjetasEmpleados, setTarjetasEmpleados] = useState([]);
    const [tarjetasLoading, setTarjetasLoading] = useState(false);
    const [tarjetaBusqueda, setTarjetaBusqueda] = useState('');
    const [tarjetaResultados, setTarjetaResultados] = useState([]);

    // Datos del backend
    const [misSolicitudes, setMisSolicitudes] = useState([]);
    const [porAprobar, setPorAprobar] = useState([]);
    const [selectedSolicitud, setSelectedSolicitud] = useState(null);

    // Modal de rechazo
    const [showRechazoModal, setShowRechazoModal] = useState(false);
    const [motivoRechazo, setMotivoRechazo] = useState('');
    const [solicitudParaRechazar, setSolicitudParaRechazar] = useState(null);

    // Estados para el formulario de solicitud
    // Hospedaje, traslados y compras específicas son opcionales: se habilitan con un check.
    const [extrasActivos, setExtrasActivos] = useState({ hospedaje: false, traslado: false, otros: false });
    // Conformidad con las condiciones de los viáticos: hay que marcarla en cada envío.
    const [aceptaCondiciones, setAceptaCondiciones] = useState(false);
    const [solicitudData, setSolicitudData] = useState({
        motivo: '',
        destino: '',
        fechaInicio: '',
        horaSalida: '',
        comidas: { desayunos: 0, almuerzos: 0, cenas: 0 },
        fechaFin: '',
        horaRegreso: '',
        // 'viaje' o 'jornada_extra' (un solo dia: motivo, fecha y horas de inicio y fin)
        tipo: 'viaje',
        horaFin: '',
        centroCosto: '',
        hospedaje: { costoPorNoche: 0 },
        traslado: { detalle: '', monto: 0 },
        otros: { detalle: '', monto: 0 },
        banco: { rif: 'J-30622535-8', banco: 'Venezolano de Credito', telefono: '0414-4423022' }
    });

    // Tarifas fijas
    const TARIFAS = {
        desayuno: 10,
        almuerzo: 18,
        cena: 15
    };

    // Estado para facturas de legalizacion
    const [facturas, setFacturas] = useState([]);
    // Pestaña activa al legalizar (desayuno, almuerzo, cena, hospedaje, traslado, otros)
    const [tabFactura, setTabFactura] = useState('desayuno');
    const [diaAbierto, setDiaAbierto] = useState(null);
    // El formulario de carga solo aparece despues de elegir que se va a subir.
    const [seleccionHecha, setSeleccionHecha] = useState(false);
    // Solicitud abierta en legalizar: para no pisarla con una respuesta que llegue tarde.
    const solicitudAbiertaRef = useRef(null);
    // Error al cargar una factura, mostrado dentro del panel en vez de un alert()
    const [errorFactura, setErrorFactura] = useState('');
    // Tasa BCV por fecha, para estimar en dolares mientras se llena la factura y
    // avisar si se pasa del tope. Se cachea por fecha para no repetir consultas.
    const [tasasPorFecha, setTasasPorFecha] = useState({});
    const [nuevaFactura, setNuevaFactura] = useState({
        fecha: '',
        concepto: '',
        monto: '',
        detalle: '',
        imagen: null,
        imagenPreview: null,
        // Comidas: segundo recibo de la misma comida (ej. la bebida en otro sitio).
        // Se carga como una sola factura con el monto de los dos sumado.
        imagen2: null,
        imagenPreview2: null,
        recibosIA: null,
        // Datos fiscales que lee la IA del recibo. La factura debe estar a nombre de
        // la empresa: rifValido en false bloquea la carga.
        razonSocial: '',
        rif: '',
        rifValido: null,
        // Fecha que la IA leyo del recibo y la prueba firmada de esa lectura. En
        // comidas la factura tiene que ser del dia elegido en el calendario.
        fechaLeidaIA: '',
        lecturaToken: '',
    });

    // Estado para comprobante de pago (reintegro)
    const [comprobantePago, setComprobantePago] = useState(null);
    const [comprobantePagoPreview, setComprobantePagoPreview] = useState(null);
    // Lectura con IA del comprobante del reintegro: idle | cargando | listo | error
    const [analisisReintegro, setAnalisisReintegro] = useState({ estado: 'idle', datos: null, error: '' });

    // Todos entran por sus propias solicitudes; a la gestion se llega con el boton
    // "Gestion CxP" o con un enlace que traiga ?tab=gestion.
    useEffect(() => {
        const tab = new URLSearchParams(window.location.search).get('tab');
        if (tab === 'gestion' && canManageViaticos && viewMode === 'list') setViewMode('gestion');
    }, [canManageViaticos, viewMode]);

    // El centro de costo lo resuelve el backend a partir del departamento, gerencia o
    // area del empleado. Antes el frontend tenia su propia copia del mapeo y su propio
    // valor por defecto (007): se desincronizo y a gente de otros centros les salia
    // 007. Ahora la regla vive en un solo lugar y aca solo se muestra.
    useEffect(() => {
        if (!user?.empleado_id) return;
        axiosInstance.get('/api/viaticos/centro-costo')
            .then(res => {
                setSolicitudData(prev => ({ ...prev, centroCosto: res.data?.centroCosto || '' }));
            })
            .catch(() => {});
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    // Gestión CxP
    const fetchGestion = useCallback(async () => {
        try {
            setGestionLoading(true);
            const res = await axiosInstance.get('/api/viaticos/gestion', { params: { estado: gestionFiltro } });
            if (res.data.success) {
                setGestionSolicitudes(res.data.solicitudes);
                setPendientesConfirmacion(res.data.pendientes_confirmacion || 0);
            }
        } catch (err) { console.error(err); }
        finally { setGestionLoading(false); }
    }, [gestionFiltro]);

    const handleConfirmarLegalizacion = async (solicitud, nota) => {
        try {
            await axiosInstance.post(`/api/viaticos/${solicitud.id}/legalizacion/confirmar`, { nota });
            fetchGestion();
            return true;
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al confirmar la legalización');
            return false;
        }
    };

    // CxP corrige, anula o restaura un pago del reintegro. Devuelve true si se guardo.
    const handleAjustarReintegro = async (solicitud, ajuste) => {
        try {
            await axiosInstance.post(`/api/viaticos/${solicitud.id}/reintegros/ajustar`, ajuste);
            fetchGestion();
            return true;
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo ajustar el pago del reintegro');
            return false;
        }
    };

    const abrirDevolverModal = (solicitud) => {
        setDevolverModal(solicitud);
        setMotivoDevolucion('');
        // Ningun pago se anula por defecto: un pago real anulado sin querer le hace
        // pagar el reintegro dos veces al empleado. CxP marca solo los que no valen.
        setReintegrosAnular([]);
    };

    const handleDevolverLegalizacion = async () => {
        if (!devolverModal || !motivoDevolucion.trim()) return;
        try {
            setSubmitting(true);
            await axiosInstance.post(`/api/viaticos/${devolverModal.id}/legalizacion/devolver`, {
                motivo: motivoDevolucion.trim(),
                anular_reintegros: reintegrosAnular,
            });
            setDevolverModal(null);
            setMotivoDevolucion('');
            fetchGestion();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al devolver la legalización');
        } finally { setSubmitting(false); }
    };

    useEffect(() => {
        if (viewMode === 'gestion') fetchGestion();
    }, [viewMode, fetchGestion]);

    const abrirPagoModal = (solicitud) => {
        setPagoModal(solicitud);
        setCapturaPago(null);
        setCapturaPagoPreview(null);
        setVerDetalleTasa(false);
        setFechaPagoInput(hoyISO());
    };

    const cerrarPagoModal = () => {
        setPagoModal(null);
        setCapturaPago(null);
        setCapturaPagoPreview(null);
        setFechaPagoInput('');
        setProyeccionTasa(null);
        setProyeccionError('');
        setVerDetalleTasa(false);
    };

    // Cada vez que cambia la fecha se pide la proyeccion de la tasa para mostrarla.
    useEffect(() => {
        if (!pagoModal || !fechaPagoInput) {
            setProyeccionTasa(null);
            setProyeccionError('');
            return;
        }
        let cancelado = false;
        setProyeccionLoading(true);
        setProyeccionError('');
        axiosInstance.get(`/api/viaticos/${pagoModal.id}/proyeccion-pago`, { params: { fecha: fechaPagoInput } })
            .then(res => { if (!cancelado) setProyeccionTasa(res.data); })
            .catch(err => {
                if (cancelado) return;
                setProyeccionTasa(null);
                setProyeccionError(err.response?.data?.error || 'No se pudo consultar la tasa');
            })
            .finally(() => { if (!cancelado) setProyeccionLoading(false); });
        return () => { cancelado = true; };
    }, [pagoModal, fechaPagoInput]);

    // Al cambiar la fecha de la factura se busca la tasa de ese dia (una sola vez).
    useEffect(() => {
        const fecha = nuevaFactura.fecha;
        if (!fecha || tasasPorFecha[fecha] !== undefined) return;
        let cancelado = false;
        axiosInstance.get('/api/tasa', { params: { fecha } })
            .then(res => {
                if (!cancelado && res.data?.tasa) {
                    setTasasPorFecha(prev => ({ ...prev, [fecha]: res.data.tasa }));
                }
            })
            .catch(() => { /* sin tasa no se muestra la estimacion; el backend valida al cargar */ });
        return () => { cancelado = true; };
    }, [nuevaFactura.fecha, tasasPorFecha]);

    const montoBsCalculado = proyeccionTasa && pagoModal
        ? (pagoModal.monto_total || 0) * proyeccionTasa.tasaAplicada
        : null;

    const handlePagar = async () => {
        if (!pagoModal || !fechaPagoInput || !proyeccionTasa) return;
        try {
            setSubmitting(true);
            const formData = new FormData();
            formData.append('fecha_pago', fechaPagoInput);
            if (capturaPago) formData.append('captura', capturaPago);
            await axiosInstance.post(`/api/viaticos/${pagoModal.id}/pagar`, formData);
            cerrarPagoModal();
            fetchGestion();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al registrar pago');
        } finally { setSubmitting(false); }
    };

    const handleAnular = async () => {
        if (!anularModal || !motivoAnulacion.trim()) return;
        try {
            setSubmitting(true);
            await axiosInstance.post(`/api/viaticos/${anularModal.id}/anular`, { motivo: motivoAnulacion.trim() });
            setAnularModal(null);
            setMotivoAnulacion('');
            fetchGestion();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al anular la solicitud');
        } finally { setSubmitting(false); }
    };

    const handleAprobarCxp = async () => {
        if (!aprobarCxpModal || !motivoAprobacionCxp.trim()) return;
        try {
            setSubmitting(true);
            await axiosInstance.post(`/api/viaticos/${aprobarCxpModal.id}/aprobar`, { forzar: true, motivo: motivoAprobacionCxp.trim() });
            setAprobarCxpModal(null);
            setMotivoAprobacionCxp('');
            fetchGestion();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al aprobar la solicitud');
        } finally { setSubmitting(false); }
    };

    // Tarjetas gerenciales (habilitar/quitar por empleado)
    const fetchTarjetas = useCallback(async () => {
        try {
            setTarjetasLoading(true);
            const res = await axiosInstance.get('/api/viaticos/tarjetas-gerenciales');
            if (res.data.success) setTarjetasEmpleados(res.data.empleados);
        } catch (err) { console.error(err); }
        finally { setTarjetasLoading(false); }
    }, []);

    useEffect(() => {
        if (showTarjetasPanel) fetchTarjetas();
    }, [showTarjetasPanel, fetchTarjetas]);

    useEffect(() => {
        const q = tarjetaBusqueda.trim();
        if (!showTarjetasPanel || q.length < 2) { setTarjetaResultados([]); return; }
        const t = setTimeout(async () => {
            try {
                const res = await axiosInstance.get('/api/viaticos/tarjetas-gerenciales', { params: { q } });
                if (res.data.success) setTarjetaResultados(res.data.resultados || []);
            } catch (err) { console.error(err); }
        }, 300);
        return () => clearTimeout(t);
    }, [tarjetaBusqueda, showTarjetasPanel]);

    const toggleTarjeta = async (empleadoId, habilitar) => {
        try {
            const url = `/api/viaticos/tarjetas-gerenciales/${empleadoId}`;
            if (habilitar) await axiosInstance.post(url);
            else await axiosInstance.delete(url);
            fetchTarjetas();
            setTarjetaResultados(prev => prev.map(e => e.id === empleadoId ? { ...e, tiene_tarjeta_gerencial: habilitar } : e));
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al actualizar la tarjeta');
        }
    };

    // IA: análisis de comprobantes
    const [analizandoIA, setAnalizandoIA] = useState(false);

    const analizarConIA = async () => {
        if (!nuevaFactura.imagen || !selectedSolicitud) return;
        try {
            setAnalizandoIA(true);
            const formData = new FormData();
            formData.append('imagen', nuevaFactura.imagen);
            if (nuevaFactura.imagen2) formData.append('imagen_2', nuevaFactura.imagen2);
            const res = await axiosInstance.post(`/api/viaticos/${selectedSolicitud.id}/comprobantes/analizar`, formData);
            if (res.data.success && res.data.datos) {
                const d = res.data.datos;
                setNuevaFactura(prev => ({
                    ...prev,
                    concepto: d.concepto || prev.concepto,
                    monto: d.monto != null ? d.monto : prev.monto,
                    // En comidas la fecha es el dia elegido; la del recibo se guarda aparte
                    // y tiene que coincidir con ese dia para poder cargarla.
                    fecha: esTipoComida(tabFactura) ? prev.fecha : (d.fecha || prev.fecha),
                    detalle: d.detalle || prev.detalle,
                    razonSocial: d.razon_social || '',
                    rif: d.rif || '',
                    rifValido: d.rif ? !!d.rif_valido : false,
                    // Con dos recibos se muestra el que no sea del dia elegido, si hay uno.
                    fechaLeidaIA: ((d.fechas || [d.fecha]).find(f => f && f !== prev.fecha)) || d.fecha || '',
                    lecturaToken: d.lectura_token || '',
                    recibosIA: d.recibos || null,
                    // Un comprobante de pago (transferencia, pago movil) no es una factura.
                    esFactura: d.es_factura !== false,
                    avisoNoFactura: d.aviso_no_factura || '',
                }));
            }
        } catch (err) {
            console.error('Error IA:', err);
            setErrorFactura(err.response?.data?.error || 'No se pudo leer la factura con IA');
        } finally {
            setAnalizandoIA(false);
        }
    };

    // ============================================
    // FETCH DATA
    // ============================================
    const fetchViaticos = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/viaticos/');
            setMisSolicitudes(response.data.mis_solicitudes || []);
            setPorAprobar(response.data.por_aprobar || []);
            setTieneTarjetaGerencial(response.data.tiene_tarjeta_gerencial || false);
        } catch (error) {
            console.error('Error fetching viaticos:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchViaticos();
    }, [fetchViaticos]);

    // ============================================
    // CALCULOS
    // ============================================
    // Las noches tampoco se escriben: salen de las fechas del viaje.
    const nochesCalculadas = useMemo(
        () => calcularNoches(solicitudData.fechaInicio, solicitudData.fechaFin),
        [solicitudData.fechaInicio, solicitudData.fechaFin]
    );
    const totalHospedaje = nochesCalculadas * solicitudData.hospedaje.costoPorNoche;
    // Las fechas y la hora de salida fijan el MAXIMO de cada comida; el empleado
    // pide cuantas quiere entre 0 y ese maximo (el backend lo vuelve a validar).
    const esJornadaExtra = !modoTarjeta && solicitudData.tipo === 'jornada_extra';
    const maxComidas = useMemo(
        () => esJornadaExtra
            // Jornada extra: un solo dia y las comidas se eligen a mano, hasta una de cada una.
            // Si arranca desde las 5:00 pm, solo cena (el backend aplica la misma regla).
            ? (!solicitudData.fechaInicio ? { desayunos: 0, almuerzos: 0, cenas: 0 }
                : solicitudData.horaSalida >= HORA_JORNADA_SOLO_CENA ? { desayunos: 0, almuerzos: 0, cenas: 1 }
                    : { desayunos: 1, almuerzos: 1, cenas: 1 })
            : calcularComidas(solicitudData.fechaInicio, solicitudData.fechaFin, solicitudData.horaSalida),
        [esJornadaExtra, solicitudData.fechaInicio, solicitudData.fechaFin, solicitudData.horaSalida]
    );
    const cambiarTipo = (tipo) => {
        setExtrasActivos({ hospedaje: false, traslado: false, otros: false });
        setSolicitudData(prev => ({
            ...prev,
            tipo,
            destino: '',
            horaFin: '',
            fechaFin: tipo === 'jornada_extra' ? prev.fechaInicio : prev.fechaFin,
            hospedaje: { costoPorNoche: 0 },
            traslado: { detalle: '', monto: 0 },
            otros: { detalle: '', monto: 0 },
        }));
    };
    // Si cambian las fechas o la hora y el maximo baja, lo pedido se recorta solo.
    const comidasElegidas = useMemo(() => ({
        desayunos: Math.min(solicitudData.comidas?.desayunos || 0, maxComidas.desayunos),
        almuerzos: Math.min(solicitudData.comidas?.almuerzos || 0, maxComidas.almuerzos),
        cenas: Math.min(solicitudData.comidas?.cenas || 0, maxComidas.cenas),
    }), [solicitudData.comidas, maxComidas]);
    const cambiarComida = (key, delta) => {
        const nuevo = Math.max(0, Math.min(maxComidas[key], comidasElegidas[key] + delta));
        setSolicitudData({ ...solicitudData, comidas: { ...comidasElegidas, [key]: nuevo } });
    };
    const alternarExtra = (clave, activo) => {
        setExtrasActivos(prev => ({ ...prev, [clave]: activo }));
        if (activo) return;
        setSolicitudData(prev => ({
            ...prev,
            ...(clave === 'hospedaje' ? { hospedaje: { ...prev.hospedaje, costoPorNoche: 0 } }
                : clave === 'traslado' ? { traslado: { detalle: '', monto: 0 } }
                    : { otros: { detalle: '', monto: 0 } }),
        }));
    };
    const totalComidas = (comidasElegidas.desayunos * TARIFAS.desayuno) +
        (comidasElegidas.almuerzos * TARIFAS.almuerzo) +
        (comidasElegidas.cenas * TARIFAS.cena);
    const totalAnticipo = totalHospedaje + parseFloat(solicitudData.traslado.monto || 0) + totalComidas + parseFloat(solicitudData.otros.monto || 0);

    const totalGastos = facturas.reduce((acc, curr) => acc + parseFloat(curr.monto || 0), 0);
    // Las facturas vienen en Bs y el backend las convierte a dolares con la tasa del
    // dia de cada una. El anticipo se aprobo en dolares, asi que la cuenta se cuadra
    // en dolares y el total en Bs queda como referencia.
    // Se suma lo RECONOCIDO (recortado al tope diario de cada comida); lo que se paso
    // del tope se acumula aparte y lo asume el empleado.
    const totalGastosUsd = facturas.reduce((acc, c) => {
        const reconocido = c.monto_usd_reconocido != null ? c.monto_usd_reconocido : c.monto_usd;
        return acc + parseFloat(reconocido || 0);
    }, 0);
    const totalExcedidoUsd = facturas.reduce((acc, c) => acc + parseFloat(c.excedente_usd || 0), 0);

    // Stats calculados
    const porLegalizar = misSolicitudes.filter(s => s.estado === 'aprobado');
    const legalizados = misSolicitudes.filter(s => s.estado === 'legalizado');
    const montoPorLegalizar = porLegalizar.reduce((acc, s) => acc + s.monto_total, 0);

    // ============================================
    // HANDLERS
    // ============================================
    const handleCreateSolicitud = async (e) => {
        e.preventDefault();
        if (!modoTarjeta && !aceptaCondiciones) {
            toast.error('Marca que has leído las condiciones de los viáticos antes de enviar.');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                motivo: solicitudData.motivo,
                destino: solicitudData.destino,
                centro_costo: solicitudData.centroCosto,
                fecha_inicio: solicitudData.fechaInicio,
                hora_salida: solicitudData.horaSalida || null,
                comidas: comidasElegidas,
                fecha_fin: esJornadaExtra ? solicitudData.fechaInicio : solicitudData.fechaFin,
                hospedaje: solicitudData.hospedaje,
                traslado: solicitudData.traslado,
                otros: solicitudData.otros,
                banco: solicitudData.banco,
                es_tarjeta_gerencial: modoTarjeta,
                tipo: esJornadaExtra ? 'jornada_extra' : 'viaje',
                hora_fin: esJornadaExtra ? (solicitudData.horaFin || null) : null,
            };
            const res = await axiosInstance.post('/api/viaticos/', payload);
            const creada = res.data;
            const eraTarjeta = modoTarjeta;
            resetForm();
            fetchViaticos();
            // Tarjeta gerencial: es postpago, va directo a subir las facturas
            if (eraTarjeta && creada?.id) {
                openLegalize(creada);
            } else {
                setViewMode('list');
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al crear solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSolicitud = async (e) => {
        e.preventDefault();
        if (!modoTarjeta && !aceptaCondiciones) {
            toast.error('Marca que has leído las condiciones de los viáticos antes de enviar.');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                motivo: solicitudData.motivo,
                destino: solicitudData.destino,
                centro_costo: solicitudData.centroCosto,
                fecha_inicio: solicitudData.fechaInicio,
                hora_salida: solicitudData.horaSalida || null,
                comidas: comidasElegidas,
                fecha_fin: esJornadaExtra ? solicitudData.fechaInicio : solicitudData.fechaFin,
                hospedaje: solicitudData.hospedaje,
                traslado: solicitudData.traslado,
                otros: solicitudData.otros,
                banco: solicitudData.banco,
                ...(esJornadaExtra ? { hora_fin: solicitudData.horaFin || null } : {}),
            };
            await axiosInstance.put(`/api/viaticos/${selectedSolicitud.id}`, payload);
            // Reenviar automaticamente
            await axiosInstance.post(`/api/viaticos/${selectedSolicitud.id}/reenviar`);
            resetForm();
            setViewMode('list');
            fetchViaticos();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al editar solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAprobar = async (solicitud) => {
        setSubmitting(true);
        try {
            await axiosInstance.post(`/api/viaticos/${solicitud.id}/aprobar`);
            fetchViaticos();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al aprobar');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRechazar = async () => {
        if (!motivoRechazo.trim()) return;
        setSubmitting(true);
        try {
            await axiosInstance.post(`/api/viaticos/${solicitudParaRechazar.id}/rechazar`, {
                motivo_rechazo: motivoRechazo
            });
            setShowRechazoModal(false);
            setMotivoRechazo('');
            setSolicitudParaRechazar(null);
            fetchViaticos();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al rechazar');
        } finally {
            setSubmitting(false);
        }
    };

    const addFactura = async () => {
        if (!nuevaFactura.concepto || !nuevaFactura.monto || !selectedSolicitud) return;
        // Las comidas van atadas a un dia del viaje: sin dia elegido no se sube.
        if (esTipoComida(tabFactura) && !nuevaFactura.fecha) return;
        setSubmitting(true);
        setErrorFactura('');
        try {
            const montoNormalizado = String(nuevaFactura.monto).replace(',', '.');
            const formData = new FormData();
            formData.append('tipo', tabFactura);
            if (nuevaFactura.razonSocial) formData.append('razon_social', nuevaFactura.razonSocial);
            if (nuevaFactura.rif) formData.append('rif', nuevaFactura.rif);
            formData.append('concepto', nuevaFactura.concepto);
            formData.append('monto', montoNormalizado);
            if (nuevaFactura.fecha) formData.append('fecha', nuevaFactura.fecha);
            if (nuevaFactura.detalle) formData.append('detalle', nuevaFactura.detalle);
            if (nuevaFactura.imagen) formData.append('imagen', nuevaFactura.imagen);
            if (nuevaFactura.imagen2) formData.append('imagen_2', nuevaFactura.imagen2);
            if (nuevaFactura.lecturaToken) formData.append('lectura_token', nuevaFactura.lecturaToken);

            const response = await axiosInstance.post(
                `/api/viaticos/${selectedSolicitud.id}/comprobantes`,
                formData
            );

            const facturasActualizadas = [...facturas, response.data];
            setFacturas(facturasActualizadas);
            setSeleccionHecha(false);
            setNuevaFactura({
                fecha: '',
                concepto: '', monto: '', detalle: '', imagen: null, imagenPreview: null, imagen2: null, imagenPreview2: null, recibosIA: null,
                razonSocial: '', rif: '', rifValido: null, fechaLeidaIA: '', lecturaToken: '',
            });
        } catch (error) {
            setErrorFactura(error.response?.data?.error || 'No se pudo cargar la factura');
        } finally {
            setSubmitting(false);
        }
    };

    const removeFactura = async (facturaId) => {
        if (!selectedSolicitud) return;
        try {
            await axiosInstance.delete(`/api/viaticos/${selectedSolicitud.id}/comprobantes/${facturaId}`);
            setFacturas(prev => prev.filter(f => f.id !== facturaId));
            return true;
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al eliminar comprobante');
            return false;
        }
    };

    const handleLegalizar = async () => {
        if (!selectedSolicitud) return;
        setSubmitting(true);
        try {
            const formData = new FormData();
            if (comprobantePago) {
                formData.append('comprobante_pago', comprobantePago);
                // Lo que la IA leyo en la vista previa: se registra eso mismo, sin releer.
                if (analisisReintegro.datos?.lectura_token) {
                    formData.append('lectura_token', analisisReintegro.datos.lectura_token);
                }
            }
            if (selectedSolicitud.fecha_fin) {
                formData.append('fecha_regreso', selectedSolicitud.fecha_fin);
            }
            await axiosInstance.post(
                `/api/viaticos/${selectedSolicitud.id}/legalizar`,
                formData
            );
            setViewMode('list');
            setSelectedSolicitud(null);
            setFacturas([]);
            setComprobantePago(null);
            setComprobantePagoPreview(null);
            setAnalisisReintegro({ estado: 'idle', datos: null, error: '' });
            fetchViaticos();
        } catch (error) {
            // Reintegro incompleto: el pago quedó registrado y la solicitud sigue en
            // Pagado. Se refresca para mostrar lo que falta y pedir el siguiente comprobante.
            if (error.response?.data?.reintegro_parcial && error.response.data.solicitud) {
                setSelectedSolicitud(error.response.data.solicitud);
                setComprobantePago(null);
                setComprobantePagoPreview(null);
                setAnalisisReintegro({ estado: 'idle', datos: null, error: '' });
                toast(error.response.data.error, { duration: 9000 });
                return;
            }
            toast.error(error.response?.data?.error || 'Error al legalizar');
        } finally {
            setSubmitting(false);
        }
    };

    const handleImagenFactura2 = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                // Cambian los recibos: la lectura anterior ya no vale.
                setNuevaFactura(prev => ({ ...prev, imagen2: file, imagenPreview2: reader.result, fechaLeidaIA: '', lecturaToken: '', recibosIA: null, esFactura: null, avisoNoFactura: '' }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImagenFactura = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                // Otra foto: la lectura anterior ya no vale, hay que volver a leerla.
                setNuevaFactura(prev => ({ ...prev, imagen: file, imagenPreview: reader.result, fechaLeidaIA: '', lecturaToken: '', recibosIA: null, esFactura: null, avisoNoFactura: '' }));
            };
            reader.readAsDataURL(file);
        }
    };

    // La IA lee el comprobante (imagen o PDF) y dice si el monto cubre lo que se le
    // debe a CxP. Si no cubre lo que falta, la solicitud no se legaliza (sigue en Pagado).
    const analizarReintegro = async (archivo) => {
        if (!archivo || !selectedSolicitud) return;
        setAnalisisReintegro({ estado: 'cargando', datos: null, error: '' });
        try {
            const formData = new FormData();
            formData.append('comprobante_pago', archivo);
            const res = await axiosInstance.post(`/api/viaticos/${selectedSolicitud.id}/reintegro/analizar`, formData);
            setAnalisisReintegro({ estado: 'listo', datos: res.data.analisis, error: '' });
        } catch (err) {
            setAnalisisReintegro({
                estado: 'error', datos: null,
                error: err.response?.data?.error || 'No se pudo leer el comprobante con IA.',
            });
        }
    };

    const handleComprobantePago = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setComprobantePago(file);
                setComprobantePagoPreview(reader.result);
            };
            reader.readAsDataURL(file);
            analizarReintegro(file);
        }
    };

    const removeComprobantePago = () => {
        setComprobantePago(null);
        setComprobantePagoPreview(null);
        setAnalisisReintegro({ estado: 'idle', datos: null, error: '' });
    };

    const resetForm = () => {
        setSolicitudData(prev => ({
            motivo: '', destino: '', fechaInicio: '', horaSalida: '', comidas: { desayunos: 0, almuerzos: 0, cenas: 0 }, fechaFin: '', horaRegreso: '', tipo: 'viaje', horaFin: '', centroCosto: prev.centroCosto,
            hospedaje: { costoPorNoche: 0 },
            traslado: { detalle: '', monto: 0 },
            otros: { detalle: '', monto: 0 },
            banco: { rif: 'J-30622535-8', banco: 'Venezolano de Credito', telefono: '0414-4423022' }
        }));
        setExtrasActivos({ hospedaje: false, traslado: false, otros: false });
        setAceptaCondiciones(false);
        setSelectedSolicitud(null);
        setModoTarjeta(false);
    };

    const openLegalize = (solicitud) => {
        setSelectedSolicitud(solicitud);
        const comprobantes = solicitud.comprobantes || [];
        setFacturas(comprobantes);
        setComprobantePago(null);
        setComprobantePagoPreview(null);
        setAnalisisReintegro({ estado: 'idle', datos: null, error: '' });
        setErrorFactura('');
        // La pestaña activa (heredada de la solicitud anterior que se haya visto en
        // esta sesion) puede no aplicar aqui, ej. esta solicitud no pidio desayuno.
        // Si no aplica, se cae a la primera pestaña que si tenga algo que cargar.
        const visibles = tabsVisiblesPara(solicitud);
        const tabInicial = visibles.some(t => t.id === tabFactura) ? tabFactura : (visibles[0]?.id || 'otros');
        setTabFactura(tabInicial);
        setDiaAbierto(null);
        setSeleccionHecha(false);
        setNuevaFactura({
            fecha: '',
            concepto: '', monto: '', detalle: '', imagen: null, imagenPreview: null, imagen2: null, imagenPreview2: null, recibosIA: null,
            razonSocial: '', rif: '', rifValido: null, fechaLeidaIA: '', lecturaToken: '',
        });
        setViewMode('legalize');

        // La lista puede estar vieja (facturas subidas despues de cargarla): se trae
        // la solicitud fresca del servidor y se reemplaza lo que se mostro.
        solicitudAbiertaRef.current = solicitud.id;
        axiosInstance.get(`/api/viaticos/${solicitud.id}`)
            .then(({ data: fresca }) => {
                setMisSolicitudes(prev => prev.map(s => (s.id === fresca.id ? fresca : s)));
                if (solicitudAbiertaRef.current !== fresca.id) return;
                setSelectedSolicitud(fresca);
                setFacturas(fresca.comprobantes || []);
            })
            .catch(err => console.error('Error refrescando la solicitud:', err));
    };

    const openDetail = (solicitud) => {
        setSelectedSolicitud(solicitud);
        setViewMode('detail');
    };

    const openEdit = (solicitud) => {
        setSelectedSolicitud(solicitud);
        setSolicitudData({
            motivo: solicitud.motivo,
            destino: solicitud.destino,
            fechaInicio: solicitud.fecha_inicio,
            horaSalida: solicitud.hora_salida || '',
            comidas: {
                desayunos: solicitud.comidas?.desayunos || 0,
                almuerzos: solicitud.comidas?.almuerzos || 0,
                cenas: solicitud.comidas?.cenas || 0,
            },
            fechaFin: solicitud.fecha_fin,
            horaRegreso: '',
            tipo: solicitud.tipo || 'viaje',
            horaFin: solicitud.hora_fin || '',
            centroCosto: solicitud.centro_costo,
            hospedaje: { costoPorNoche: solicitud.hospedaje?.costoPorNoche || 0 },
            traslado: solicitud.traslado || { detalle: '', monto: 0 },
            otros: solicitud.otros || { detalle: '', monto: 0 },
            banco: solicitud.banco || { rif: '', banco: '', telefono: '' }
        });
        setExtrasActivos({
            hospedaje: (solicitud.hospedaje?.costoPorNoche || 0) > 0,
            traslado: (solicitud.traslado?.monto || 0) > 0 || !!solicitud.traslado?.detalle,
            otros: (solicitud.otros?.monto || 0) > 0 || !!solicitud.otros?.detalle,
        });
        setAceptaCondiciones(false);
        setViewMode('edit');
    };

    // ============================================
    // RENDER: LISTA
    // ============================================
    const renderList = () => (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center flex-wrap gap-4">
                <h2 className="page-title">Solicitudes de Viáticos</h2>
            </div>
            {/* Stats Header */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <StatCard
                    title="Por Legalizar"
                    value={`$${montoPorLegalizar.toFixed(2)}`}
                    icon={Clock}
                    color="bg-amber-500"
                    subtext={`${porLegalizar.length} solicitud(es) pendiente(s)`}
                />
                <StatCard
                    title="Solicitudes Totales"
                    value={misSolicitudes.length}
                    icon={FileText}
                    color="bg-blue-500"
                    subtext="Todas tus solicitudes"
                />
                <StatCard
                    title="Legalizadas"
                    value={legalizados.length}
                    icon={CheckCircle}
                    color="bg-emerald-500"
                    subtext="Completadas"
                />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Mis Solicitudes</h3>
                <div className="flex items-center gap-3">
                    {canManageViaticos && (
                        <button
                            onClick={() => setViewMode('gestion')}
                            className="flex items-center gap-2 px-5 py-3 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors text-indigo-700 font-medium"
                        >
                            <CreditCard size={18} />
                            Gestión CxP
                        </button>
                    )}
                    {tieneTarjetaGerencial && (
                        <motion.button
                            onClick={() => { resetForm(); setModoTarjeta(true); setViewMode('create'); }}
                            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all"
                            style={{ background: 'linear-gradient(135deg, #e3b53b 0%, #f4d160 45%, #c8991f 100%)', boxShadow: '0 10px 20px -6px rgba(200,153,31,0.5)' }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <CreditCard size={20} />
                            Legalizar con tarjeta
                        </motion.button>
                    )}
                    <motion.button
                        onClick={() => { resetForm(); setViewMode('create'); }}
                        className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors shadow-lg shadow-gray-900/20"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <Plus size={20} />
                        Nueva Solicitud
                    </motion.button>
                </div>
            </div>

            {/* Lista unificada */}
            {renderSolicitudesUnificadas()}
        </div>
    );

    const approvalsRef = React.useRef(null);

    // Scroll a la sección de aprobaciones si viene de una notificación
    React.useEffect(() => {
        if (scrollToApprovals && porAprobar.length > 0 && approvalsRef.current) {
            setTimeout(() => {
                approvalsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setScrollToApprovals(false);
            }, 300);
        }
    }, [scrollToApprovals, porAprobar]);

    const renderSolicitudesUnificadas = () => (
        <div className="space-y-6">
            {/* Solicitudes por aprobar (si es supervisor) */}
            {porAprobar.length > 0 && (
                <div ref={approvalsRef}>
                    <div className="flex items-center gap-2 mb-3">
                        <Users size={16} className="text-amber-500" />
                        <h4 className="text-sm font-semibold text-amber-700 uppercase tracking-wide">Requieren tu aprobación</h4>
                        <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">{porAprobar.length}</span>
                    </div>
                    <div className="grid gap-3">
                        {porAprobar.map((solicitud) => (
                            <motion.div
                                key={`aprobar-${solicitud.id}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-amber-50 p-5 rounded-2xl border border-amber-200"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 rounded-xl bg-amber-100 text-amber-600">
                                            <User size={22} />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-bold text-gray-900">{solicitud.empleado_nombre}</h3>
                                                <span className="text-sm text-gray-400">{solicitud.codigo}</span>
                                            </div>
                                            <p className="text-sm text-gray-700 mt-0.5">{solicitud.motivo}</p>
                                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                                <span className="flex items-center gap-1"><MapPin size={14} /> {solicitud.destino}</span>
                                                <span className="flex items-center gap-1"><Calendar size={14} /> {solicitud.fecha_inicio} - {solicitud.fecha_fin}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <p className="text-xl font-bold text-gray-900 mr-2">${solicitud.monto_total.toFixed(2)}</p>
                                        <motion.button
                                            onClick={() => handleAprobar(solicitud)}
                                            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors font-medium text-sm"
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            disabled={submitting}
                                        >
                                            <ThumbsUp size={15} /> Aprobar
                                        </motion.button>
                                        <motion.button
                                            onClick={() => { setSolicitudParaRechazar(solicitud); setShowRechazoModal(true); }}
                                            className="flex items-center gap-2 px-4 py-2 bg-white text-red-500 border border-red-200 rounded-xl hover:bg-red-50 transition-colors font-medium text-sm"
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <ThumbsDown size={15} /> Rechazar
                                        </motion.button>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}

            {/* Mis solicitudes */}
            <div className="grid gap-4">
                {loading ? (
                    <div className="text-center py-16 text-gray-400">Cargando...</div>
                ) : misSolicitudes.length === 0 && porAprobar.length === 0 ? (
                    <div className="text-center py-16 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                        <Receipt size={48} className="text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">No tienes solicitudes de viáticos</p>
                        <p className="text-gray-400 text-sm mt-1">Crea una nueva solicitud para comenzar</p>
                    </div>
                ) : (
                    misSolicitudes.map((solicitud) => (
                        <motion.div
                            key={solicitud.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-md transition-all group cursor-pointer"
                            onClick={() => {
                                if (solicitud.estado === 'aprobado' || solicitud.estado === 'pagado') openLegalize(solicitud);
                                else if (solicitud.estado === 'rechazado') openEdit(solicitud);
                                else openDetail(solicitud);
                            }}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className={`p-3 rounded-xl ${
                                        solicitud.estado === 'pendiente' ? 'bg-amber-100 text-amber-600' :
                                        solicitud.estado === 'aprobado' ? 'bg-blue-100 text-blue-600' :
                                        solicitud.estado === 'rechazado' ? 'bg-red-100 text-red-600' :
                                        solicitud.estado === 'pagado' ? 'bg-indigo-100 text-indigo-600' :
                                        solicitud.estado === 'anulado' ? 'bg-slate-100 text-slate-500' :
                                        'bg-emerald-100 text-emerald-600'
                                    }`}>
                                        {solicitud.estado === 'pendiente' ? <Clock size={24} /> :
                                         solicitud.estado === 'aprobado' ? <DollarSign size={24} /> :
                                         solicitud.estado === 'rechazado' ? <XCircle size={24} /> :
                                         solicitud.estado === 'pagado' ? <CreditCard size={24} /> :
                                         solicitud.estado === 'anulado' ? <Ban size={24} /> :
                                         <CheckCircle size={24} />}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-bold text-gray-900">{solicitud.motivo}</h3>
                                            <span className="text-sm text-gray-400">{solicitud.codigo}</span>
                                            {solicitud.es_tarjeta_gerencial && (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#fbf1d3] text-[#8a6d1a] border border-[#e6cf7a]">
                                                    <CreditCard size={11} /> Tarjeta gerencial
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                            <span className="flex items-center gap-1"><MapPin size={14} /> {solicitud.destino}</span>
                                            <span className="flex items-center gap-1"><Calendar size={14} /> {solicitud.fecha_inicio}</span>
                                        </div>
                                        {solicitud.estado === 'rechazado' && solicitud.motivo_rechazo && (
                                            <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                                                <AlertCircle size={12} />
                                                Rechazado: {solicitud.motivo_rechazo}
                                            </p>
                                        )}
                                        {solicitud.estado === 'anulado' && solicitud.motivo_anulacion && (
                                            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                                                <Ban size={12} />
                                                Anulado: {solicitud.motivo_anulacion}
                                            </p>
                                        )}
                                        {solicitud.legalizacion_devuelta && (
                                            <p className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                                                <AlertCircle size={12} />
                                                CxP devolvió la legalización: {solicitud.legalizacion_devuelta.motivo}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-bold text-gray-900">
                                        {solicitud.es_tarjeta_gerencial
                                            ? (solicitud.monto_legalizado ? `Bs. ${solicitud.monto_legalizado.toLocaleString('es-VE', { minimumFractionDigits: 2 })}` : '—')
                                            : `$${solicitud.monto_total.toFixed(2)}`}
                                    </p>
                                    <div className="flex items-center justify-end gap-2 mt-1">
                                        <EstadoBadge estado={solicitud.estado} legalizacion={solicitud.legalizacion_estado} />
                                        {(['aprobado', 'pagado'].includes(solicitud.estado)) && (
                                            <ChevronRight size={16} className="text-gray-400 group-hover:translate-x-1 transition-transform" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))
                )}
            </div>
        </div>
    );

    // ============================================
    // RENDER: DETALLE (solo lectura)
    // ============================================
    const renderDetail = () => {
        if (!selectedSolicitud) return null;
        const s = selectedSolicitud;
        return (
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-4 mb-8">
                    <button onClick={() => setViewMode('list')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <ArrowLeft size={24} className="text-gray-600" />
                    </button>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-gray-900">Detalle de Solicitud</h1>
                        <p className="text-gray-500">{s.codigo}</p>
                    </div>
                    <EstadoBadge estado={s.estado} legalizacion={s.legalizacion_estado} />
                    <button
                        onClick={async () => {
                            try {
                                const res = await axiosInstance.get(`/api/viaticos/${s.id}/pdf`, { responseType: 'blob' });
                                const url = window.URL.createObjectURL(new Blob([res.data]));
                                const a = document.createElement('a');
                                a.href = url; a.download = `Viatico_${s.codigo}.pdf`; a.click();
                                window.URL.revokeObjectURL(url);
                            } catch (err) { console.error('Error downloading PDF:', err); }
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl transition-colors text-sm font-medium"
                    >
                        <FileDown size={16} /> PDF
                    </button>
                </div>

                <div className="space-y-6">
                    <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                        <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <Briefcase size={20} className="text-blue-500" /> {s.es_jornada_extra ? 'Detalles de la Jornada Extra' : 'Detalles del Viaje'}
                        </h2>
                        <div className="grid grid-cols-2 gap-6">
                            <div>
                                <p className="text-sm text-gray-500">Motivo</p>
                                <p className="font-medium text-gray-900 mt-1">{s.motivo}</p>
                            </div>
                            {s.es_jornada_extra ? (
                                <div>
                                    <p className="text-sm text-gray-500">Jornada extra</p>
                                    <p className="font-medium text-gray-900 mt-1">
                                        {s.fecha_inicio}
                                        {s.hora_salida && <span className="text-gray-500 font-normal"> · de {s.hora_salida} a {s.hora_fin || '—'}</span>}
                                    </p>
                                </div>
                            ) : (
                            <div>
                                <p className="text-sm text-gray-500">Destino</p>
                                <p className="font-medium text-gray-900 mt-1">{s.destino}</p>
                            </div>
                            )}
                            {!s.es_jornada_extra && (
                            <>
                            <div>
                                <p className="text-sm text-gray-500">Fecha inicio</p>
                                <p className="font-medium text-gray-900 mt-1">
                                    {s.fecha_inicio}
                                    {s.hora_salida && <span className="text-gray-500 font-normal"> · sale {s.hora_salida}</span>}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Fecha fin</p>
                                <p className="font-medium text-gray-900 mt-1">
                                    {s.fecha_fin}
                                </p>
                            </div>
                            </>
                            )}
                            <div>
                                <p className="text-sm text-gray-500">Aprobador</p>
                                <p className="font-medium text-gray-900 mt-1">{s.aprobador_nombre || 'No asignado'}</p>
                                {s.aprobado_por_cxp_nombre && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        Aprobado por CxP ({s.aprobado_por_cxp_nombre}) en su lugar: {s.motivo_aprobacion_cxp}
                                    </p>
                                )}
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Monto Total</p>
                                <p className="font-bold text-2xl text-gray-900 mt-1">${s.monto_total.toFixed(2)}</p>
                            </div>
                        </div>
                    </div>

                    {s.estado === 'rechazado' && s.motivo_rechazo && (
                        <div className="bg-red-50 p-6 rounded-3xl border border-red-200">
                            <div className="flex items-center gap-2 text-red-700 font-bold mb-2">
                                <XCircle size={20} /> Motivo de Rechazo
                            </div>
                            <p className="text-red-600">{s.motivo_rechazo}</p>
                        </div>
                    )}

                    {s.estado === 'legalizado' && s.legalizacion_estado && (
                        <div className={`p-4 rounded-2xl border text-sm ${s.legalizacion_estado === 'pendiente_cxp'
                            ? 'bg-amber-50 border-amber-200 text-amber-800'
                            : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
                            {s.legalizacion_estado === 'pendiente_cxp'
                                ? 'Legalizado · pendiente de confirmación de CxP.'
                                : `Legalizado · cerrado por CxP${s.legalizacion_confirmada_por ? ` (${s.legalizacion_confirmada_por}` : ''}${s.legalizacion_confirmada_por && s.legalizacion_confirmada_fecha ? `, ${new Date(s.legalizacion_confirmada_fecha).toLocaleDateString('es-VE')})` : s.legalizacion_confirmada_por ? ')' : ''}.`}
                        </div>
                    )}

                    {s.legalizacion_devuelta && (
                        <div className="bg-amber-50 p-6 rounded-3xl border border-amber-200">
                            <div className="flex items-center gap-2 text-amber-800 font-bold mb-2">
                                <AlertCircle size={20} /> CxP devolvió tu legalización
                            </div>
                            <p className="text-amber-700">{s.legalizacion_devuelta.motivo}</p>
                            <p className="text-xs text-amber-600 mt-2">Corrige lo indicado y vuelve a enviar la legalización.</p>
                        </div>
                    )}

                    {/* Desglose de gastos */}
                    <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                        <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                            <DollarSign size={20} className="text-emerald-500" /> Desglose de Gastos
                        </h2>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                                <span className="flex items-center gap-2 text-gray-700"><Moon size={16} className="text-indigo-500" /> Hospedaje ({s.hospedaje.noches} noches)</span>
                                <span className="font-bold">${(s.hospedaje.noches * s.hospedaje.costoPorNoche).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                                <span className="flex items-center gap-2 text-gray-700"><Bus size={16} className="text-blue-500" /> Traslados</span>
                                <span className="font-bold">${(s.traslado.monto || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                                <span className="flex items-center gap-2 text-gray-700"><Utensils size={16} className="text-orange-500" /> Comidas ({s.comidas.desayunos}D + {s.comidas.almuerzos}A + {s.comidas.cenas}C)</span>
                                <span className="font-bold">${((s.comidas.desayunos * TARIFAS.desayuno) + (s.comidas.almuerzos * TARIFAS.almuerzo) + (s.comidas.cenas * TARIFAS.cena)).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
                                <span className="flex items-center gap-2 text-gray-700"><ShoppingBag size={16} className="text-pink-500" /> Compras Especificas</span>
                                <span className="font-bold">${(s.otros.monto || 0).toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================
    // RENDER: FORMULARIO (crear / editar)
    // ============================================
    const renderForm = (isEdit = false) => (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => { resetForm(); setViewMode('list'); }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                    <ArrowLeft size={24} className="text-gray-600" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {isEdit ? 'Corregir y Reenviar Solicitud' : modoTarjeta ? 'Legalizar con tarjeta gerencial' : 'Nueva Solicitud de Viáticos'}
                    </h1>
                    {isEdit && selectedSolicitud && (
                        <p className="text-gray-500">{selectedSolicitud.codigo}</p>
                    )}
                </div>
            </div>

            {isEdit && selectedSolicitud?.motivo_rechazo && (
                <div className="bg-red-50 p-5 rounded-2xl border border-red-200 mb-6 flex items-start gap-3">
                    <XCircle size={20} className="text-red-500 mt-0.5 shrink-0" />
                    <div>
                        <p className="font-medium text-red-700">Motivo del rechazo:</p>
                        <p className="text-red-600 text-sm mt-1">{selectedSolicitud.motivo_rechazo}</p>
                    </div>
                </div>
            )}

            {modoTarjeta && (
                <div
                    className="mb-6 p-4 rounded-2xl border flex items-start gap-2"
                    style={{ backgroundColor: '#fdf6e3', borderColor: '#e6cf7a', color: '#8a6d1a' }}
                >
                    <CreditCard size={18} className="mt-0.5 shrink-0" />
                    <p className="text-sm font-medium">
                        Legalización postpago con tarjeta gerencial: el gasto ya lo pagaste con la tarjeta. Registra el viaje y en el siguiente paso subes las facturas. No lleva anticipo ni pago de CxP.
                    </p>
                </div>
            )}

            <form onSubmit={isEdit ? handleEditSolicitud : handleCreateSolicitud} className="space-y-8">
                {/* 1. Datos del Viaje */}
                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900">
                            <Briefcase size={20} className="text-blue-500" />
                            {esJornadaExtra ? 'Detalles de la Jornada Extra' : 'Detalles del Viaje'}
                        </h2>
                        {/* El tipo se elige al crear; al corregir una rechazada se mantiene. */}
                        {!modoTarjeta && !isEdit && (
                            <div className="flex bg-gray-100 p-1 rounded-xl">
                                {[{ id: 'viaje', label: 'Viaje' }, { id: 'jornada_extra', label: 'Jornada extra' }].map(op => (
                                    <button
                                        key={op.id}
                                        type="button"
                                        onClick={() => solicitudData.tipo !== op.id && cambiarTipo(op.id)}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${solicitudData.tipo === op.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        {op.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {esJornadaExtra ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Motivo de la jornada extra</label>
                            <input
                                type="text"
                                required
                                value={solicitudData.motivo}
                                onChange={e => setSolicitudData({ ...solicitudData, motivo: e.target.value })}
                                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-base"
                                placeholder="Ej: Cierre de inventario"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Fecha</label>
                            <input
                                type="date"
                                required
                                value={solicitudData.fechaInicio}
                                onChange={e => setSolicitudData({ ...solicitudData, fechaInicio: e.target.value, fechaFin: e.target.value })}
                                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none text-base"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Centro de Costo</label>
                            <input
                                type="text"
                                value={solicitudData.centroCosto}
                                readOnly
                                className="w-full p-3.5 bg-gray-100 border border-gray-200 rounded-xl text-gray-500 cursor-not-allowed text-base"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hora de inicio</label>
                            <input
                                type="time"
                                required
                                value={solicitudData.horaSalida}
                                onChange={e => setSolicitudData({ ...solicitudData, horaSalida: e.target.value })}
                                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none text-base"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hora de fin</label>
                            <input
                                type="time"
                                required
                                value={solicitudData.horaFin}
                                onChange={e => setSolicitudData({ ...solicitudData, horaFin: e.target.value })}
                                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none text-base"
                            />
                            {solicitudData.horaSalida && solicitudData.horaFin && solicitudData.horaFin <= solicitudData.horaSalida && (
                                <p className="text-xs text-red-500 mt-1.5">Tiene que ser después de la hora de inicio.</p>
                            )}
                        </div>
                    </div>
                    ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Motivo del Viaje</label>
                            <input
                                type="text"
                                required
                                value={solicitudData.motivo}
                                onChange={e => setSolicitudData({ ...solicitudData, motivo: e.target.value })}
                                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-base"
                                placeholder="Ej: Visita tecnica a Sede Caracas"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Sede de Destino</label>
                            <input
                                type="text"
                                required
                                value={solicitudData.destino}
                                onChange={e => setSolicitudData({ ...solicitudData, destino: e.target.value })}
                                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none text-base"
                                placeholder="Ciudad o Sede"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Centro de Costo</label>
                            <input
                                type="text"
                                value={solicitudData.centroCosto}
                                readOnly
                                className="w-full p-3.5 bg-gray-100 border border-gray-200 rounded-xl text-gray-500 cursor-not-allowed text-base"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Desde</label>
                            <input
                                type="date"
                                required
                                value={solicitudData.fechaInicio}
                                onChange={e => setSolicitudData({ ...solicitudData, fechaInicio: e.target.value })}
                                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none text-base"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hora de salida (inicio del viaje)</label>
                            <input
                                type="time"
                                required
                                value={solicitudData.horaSalida}
                                onChange={e => setSolicitudData({ ...solicitudData, horaSalida: e.target.value })}
                                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none text-base"
                            />
                            <p className="text-xs text-gray-400 mt-1.5">La hora a la que sales el primer dia. De ella depende si te toca desayuno y almuerzo ese dia.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hasta</label>
                            <input
                                type="date"
                                required
                                value={solicitudData.fechaFin}
                                onChange={e => setSolicitudData({ ...solicitudData, fechaFin: e.target.value })}
                                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none text-base"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hora estimada de regreso</label>
                            <input
                                type="time"
                                value={solicitudData.horaRegreso}
                                onChange={e => setSolicitudData({ ...solicitudData, horaRegreso: e.target.value })}
                                className="w-full p-3.5 bg-gray-50 border border-gray-200 rounded-xl outline-none text-base"
                            />
                            <p className="text-xs text-gray-400 mt-1.5">Opcional.</p>
                        </div>
                    </div>
                    )}
                </div>

                {/* 2. Estimacion de Gastos (no aplica a tarjeta gerencial) */}
                {!modoTarjeta && (
                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                    <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 mb-6">
                        <DollarSign size={20} className="text-emerald-500" />
                        Estimacion de Gastos
                    </h2>

                    <div className="space-y-6">
                        {/* Comidas */}
                        <div className="bg-gray-50 p-5 rounded-2xl">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                                    <Utensils size={18} className="text-orange-500" /> Viaticos de Comida
                                </h3>
                                <span className="text-xl font-bold text-gray-900">${totalComidas.toFixed(2)}</span>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                {[
                                    { key: 'desayunos', label: 'Desayunos', tarifa: TARIFAS.desayuno },
                                    { key: 'almuerzos', label: 'Almuerzos', tarifa: TARIFAS.almuerzo },
                                    { key: 'cenas', label: 'Cenas', tarifa: TARIFAS.cena }
                                ].map(({ key, label, tarifa }) => (
                                    <div key={key} className="p-4 bg-white rounded-xl border border-gray-200 text-center">
                                        <span className="block text-xs font-medium text-gray-500 mb-2">{label} (${tarifa})</span>
                                        <div className="flex items-center justify-center gap-3">
                                            <button
                                                type="button"
                                                onClick={() => cambiarComida(key, -1)}
                                                disabled={comidasElegidas[key] <= 0}
                                                className="w-9 h-9 rounded-full border border-gray-200 text-lg font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                aria-label={`Quitar un ${label.toLowerCase()}`}
                                            >−</button>
                                            <span className="block font-bold text-2xl text-gray-900 w-8">{comidasElegidas[key]}</span>
                                            <button
                                                type="button"
                                                onClick={() => cambiarComida(key, 1)}
                                                disabled={comidasElegidas[key] >= maxComidas[key]}
                                                className="w-9 h-9 rounded-full border border-gray-200 text-lg font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                aria-label={`Agregar un ${label.toLowerCase()}`}
                                            >+</button>
                                        </div>
                                        <span className="block text-[11px] text-gray-400 mt-2">Máximo: {maxComidas[key]}</span>
                                    </div>
                                ))}
                            </div>

                            <p className="text-xs text-gray-500 mt-4">
                                {esJornadaExtra ? (
                                    !solicitudData.fechaInicio
                                        ? 'Elige la fecha de la jornada extra para pedir las comidas.'
                                        : solicitudData.horaSalida >= HORA_JORNADA_SOLO_CENA
                                            ? `La jornada empieza a las ${solicitudData.horaSalida}, desde las 5:00 pm, así que solo puedes pedir la cena.`
                                            : 'Marca solo las comidas que vas a necesitar durante la jornada extra: una de cada una como máximo.'
                                ) : !solicitudData.fechaInicio || !solicitudData.fechaFin ? (
                                    'Elige las fechas del viaje y la hora de salida para ver cuántas comidas puedes pedir.'
                                ) : (
                                    <>
                                        Pide solo las comidas que necesites: puedes dejar en 0 la que no vayas a usar. El máximo es una de cada una por día del viaje.
                                        {!solicitudData.horaSalida ? ' Indica la hora de salida para ajustar el primer dia.'
                                            : solicitudData.horaSalida < HORA_TOPE_DESAYUNO ? ` Sales a las ${solicitudData.horaSalida}, antes de las 8:00, asi que el primer dia puede llevar las tres comidas.`
                                                : solicitudData.horaSalida < HORA_TOPE_ALMUERZO ? ` Sales a las ${solicitudData.horaSalida}, despues de las 8:00, asi que el primer dia no incluye desayuno.`
                                                    : ` Sales a las ${solicitudData.horaSalida}, despues de las 12:00, asi que el primer dia solo incluye cena.`}
                                    </>
                                )}
                            </p>
                        </div>

                        {/* Gastos opcionales: se habilitan con un check para no ocupar pantalla.
                            La jornada extra solo lleva comidas. */}
                        {!esJornadaExtra && (
                        <div className="space-y-3">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700">Otros gastos</h3>
                                <p className="text-xs text-gray-400">Marca solo los que necesites para este viaje.</p>
                            </div>

                            {/* Hospedaje */}
                            <GastoOpcional
                                activo={extrasActivos.hospedaje}
                                onToggle={activo => alternarExtra('hospedaje', activo)}
                                icon={Moon} iconColor="text-indigo-500" titulo="Hospedaje"
                                total={totalHospedaje}
                            >
                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <label className="text-xs text-gray-500 block mb-1.5">Noches</label>
                                        <div className="w-full p-3 rounded-xl border border-gray-200 bg-gray-100 text-gray-500 text-base">
                                            {nochesCalculadas}
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <label className="text-xs text-gray-500 block mb-1.5">Costo por Noche ($)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={solicitudData.hospedaje.costoPorNoche}
                                            onChange={e => setSolicitudData({ ...solicitudData, hospedaje: { ...solicitudData.hospedaje, costoPorNoche: parseFloat(e.target.value) || 0 } })}
                                            className="w-full p-3 rounded-xl border border-gray-200 text-base bg-white"
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-3">
                                    {!solicitudData.fechaInicio || !solicitudData.fechaFin
                                        ? 'Las noches salen de las fechas del viaje. Solo indica el costo por noche.'
                                        : nochesCalculadas === 0
                                            ? 'El viaje empieza y termina el mismo dia, asi que no lleva hospedaje.'
                                            : `Del ${solicitudData.fechaInicio} al ${solicitudData.fechaFin} duerme ${nochesCalculadas} ${nochesCalculadas === 1 ? 'noche' : 'noches'} fuera. Solo indica el costo por noche.`}
                                </p>
                            </GastoOpcional>

                            {/* Traslados */}
                            <GastoOpcional
                                activo={extrasActivos.traslado}
                                onToggle={activo => alternarExtra('traslado', activo)}
                                icon={Bus} iconColor="text-blue-500" titulo="Traslados"
                                total={parseFloat(solicitudData.traslado.monto) || 0}
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <input
                                        type="text"
                                        value={solicitudData.traslado.detalle}
                                        onChange={e => setSolicitudData({ ...solicitudData, traslado: { ...solicitudData.traslado, detalle: e.target.value } })}
                                        className="sm:col-span-2 w-full p-3 bg-white rounded-xl border border-gray-200 text-sm"
                                        placeholder="Detalle (Ej: Taxi Aeropuerto - Oficina)"
                                    />
                                    <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-3">
                                        <span className="text-gray-400 text-sm">$</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={solicitudData.traslado.monto}
                                            onChange={e => setSolicitudData({ ...solicitudData, traslado: { ...solicitudData.traslado, monto: parseFloat(e.target.value) || 0 } })}
                                            className="w-full py-3 bg-transparent text-base font-semibold text-gray-900 focus:outline-none"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                            </GastoOpcional>

                            {/* Compras Especificas */}
                            <GastoOpcional
                                activo={extrasActivos.otros}
                                onToggle={activo => alternarExtra('otros', activo)}
                                icon={ShoppingBag} iconColor="text-pink-500" titulo="Compras Especificas"
                                total={parseFloat(solicitudData.otros.monto) || 0}
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <input
                                        type="text"
                                        value={solicitudData.otros.detalle}
                                        onChange={e => setSolicitudData({ ...solicitudData, otros: { ...solicitudData.otros, detalle: e.target.value } })}
                                        className="sm:col-span-2 w-full p-3 bg-white rounded-xl border border-gray-200 text-sm"
                                        placeholder="Detalle de la compra"
                                    />
                                    <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-3">
                                        <span className="text-gray-400 text-sm">$</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={solicitudData.otros.monto}
                                            onChange={e => setSolicitudData({ ...solicitudData, otros: { ...solicitudData.otros, monto: parseFloat(e.target.value) || 0 } })}
                                            className="w-full py-3 bg-transparent text-base font-semibold text-gray-900 focus:outline-none"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>
                            </GastoOpcional>
                        </div>
                        )}
                    </div>

                    {/* Total Footer */}
                    <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-gray-500 text-lg">Total Anticipo Requerido</span>
                        <div className="text-3xl font-bold text-emerald-600 bg-emerald-50 px-8 py-3 rounded-2xl">
                            ${totalAnticipo.toFixed(2)}
                        </div>
                    </div>
                </div>
                )}

                {/* Condiciones de los viáticos (no aplica a tarjeta gerencial: no lleva anticipo) */}
                {!modoTarjeta && (
                <div className="bg-red-50 border border-red-200 p-6 sm:p-8 rounded-3xl">
                    <h2 className="flex items-center gap-2 text-lg font-bold text-red-900 mb-4">
                        <AlertCircle size={20} className="text-red-600" />
                        Nota Importante
                    </h2>

                    <ul className="space-y-3 text-sm text-red-900 list-disc pl-5">
                        <li>No se consideran gastos de viáticos: <strong>bebidas alcohólicas, golosinas, productos de aseo personal</strong>.</li>
                        <li>Aquellos gastos de viáticos que excedan el monto estipulado en la Normativa para el Proceso de Relaciones de Gastos, no serán tomados en cuenta para el cierre de anticipos y/o reembolsos.</li>
                        <li>Los montos de los viáticos antes mencionados no son acumulativos, es decir, deben ser consumidos de manera individual.</li>
                        <li>Las facturas deben ser entregadas al área de Cuentas por Pagar.</li>
                    </ul>

                    <label className="flex items-start gap-3 mt-6 pt-5 border-t border-red-200 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={aceptaCondiciones}
                            onChange={e => setAceptaCondiciones(e.target.checked)}
                            className="w-5 h-5 mt-0.5 rounded border-red-300 accent-gray-900 cursor-pointer shrink-0"
                        />
                        <span className="text-sm font-semibold text-red-900">
                            He leído estas condiciones
                            {!aceptaCondiciones && <span className="block text-xs font-normal text-red-700/80 mt-0.5">Marca la casilla para poder enviar la solicitud.</span>}
                        </span>
                    </label>
                </div>
                )}

                {/* Footer buttons */}
                <div className="flex gap-4">
                    <button
                        type="button"
                        onClick={() => { resetForm(); setViewMode('list'); }}
                        className="flex-1 py-4 text-gray-600 font-semibold hover:bg-gray-50 rounded-2xl transition-colors text-base"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={submitting || (!modoTarjeta && !aceptaCondiciones)}
                        className="flex-1 py-4 bg-gray-900 text-white font-bold rounded-2xl shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all disabled:opacity-50 text-base flex items-center justify-center gap-2"
                    >
                        {submitting ? (
                            <RefreshCw size={20} className="animate-spin" />
                        ) : isEdit ? (
                            <><Send size={20} /> Corregir y Reenviar</>
                        ) : modoTarjeta ? (
                            <><ChevronRight size={20} /> Continuar a facturas</>
                        ) : (
                            <><Send size={20} /> Generar Solicitud</>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );

    // ============================================
    // RENDER: LEGALIZACION (ampliada)
    // ============================================
    const renderLegalize = () => {
        if (!selectedSolicitud) return null;

        const esTarjeta = selectedSolicitud.es_tarjeta_gerencial;
        const sinAbono = !selectedSolicitud.monto_bs || selectedSolicitud.monto_bs <= 0;
        // La cuenta se cuadra en dolares: anticipo aprobado contra las facturas ya
        // convertidas con la tasa del dia de cada una.
        const montoReferencia = selectedSolicitud.monto_total || 0;
        const saldo = montoReferencia - totalGastosUsd;

        // Lo que se maneja es en bolivares: cada factura viene en Bs y lo que se
        // reconoce / excede se reparte con la misma proporcion que ya sale en dolares
        // (usa la tasa de la factura), asi reconocido + excedido = total de facturas.
        // Sin conversion a dolares todavia (monto_usd vacio) todo cuenta como reconocido.
        const reparteBs = (c, usdParte, esExcedente) => {
            const bs = parseFloat(c.monto || 0);
            const usd = parseFloat(c.monto_usd || 0);
            if (usd <= 0) return esExcedente ? 0 : bs;
            return bs * (parseFloat(usdParte || 0) / usd);
        };
        const reconocidoUsdDe = (c) => (c.monto_usd_reconocido != null ? c.monto_usd_reconocido : c.monto_usd);
        const reconocidoBs = facturas.reduce((acc, c) => acc + reparteBs(c, reconocidoUsdDe(c), false), 0);
        const excedidoBs = facturas.reduce((acc, c) => acc + reparteBs(c, c.excedente_usd, true), 0);
        // Saldo en bolivares: lo abonado por CxP menos lo reconocido de las facturas.
        // Sin abono todavia no hay base en Bs y se queda en dolares.
        const saldoBs = sinAbono ? null : (selectedSolicitud.monto_bs || 0) - reconocidoBs;
        const usdFmt = (v) => `$ ${v.toLocaleString('es-VE', { minimumFractionDigits: 2 })}`;
        // Direccion del saldo y si hace falta comprobante del reintegro: se decide con el
        // saldo en Bs, igual que el servidor (que perdona hasta Bs. 1,00 de redondeo).
        // Sin abono todavia no hay Bs y se usa el saldo en dolares.
        const saldoDir = saldoBs != null ? saldoBs : saldo;
        // Lo que ya devolvió en transferencias anteriores se descuenta. Lo calcula el
        // backend, que no cuenta los pagos que CxP anuló.
        const yaReintegradoBs = selectedSolicitud.reintegrado_bs || 0;
        const pendienteBs = saldoBs != null ? saldoBs - yaReintegradoBs : null;
        const requiereReintegro = pendienteBs != null ? pendienteBs > 1 : saldo > 0;

        // Que comida le toca cada dia: lo calcula el backend a partir de las fechas
        // del viaje y la hora de salida.
        const planComidas = selectedSolicitud.plan_comidas || [];
        const facturasDelTab = facturas.filter(f => (f.tipo || 'otros') === tabFactura);
        // Cuantas comidas de cada tipo cubrio el anticipo: lo que el empleado pidio,
        // que puede ser menos que los dias del viaje (incluso 0).
        const aprobadasDe = (tipo) => aprobadasDeSolicitud(selectedSolicitud, tipo);
        // Cupo por tipo de comida (no solo el de la pestaña activa): lo usa el
        // calendario, que muestra los 3 tipos de comida a la vez por dia.
        const cupoLlenoDe = (tipo) => facturas.filter(f => (f.tipo || 'otros') === tipo).length >= aprobadasDe(tipo);
        const tabsVisibles = tabsVisiblesPara(selectedSolicitud);
        const mostrarFormulario = seleccionHecha && (!esTipoComida(tabFactura) || !!nuevaFactura.fecha);
        // Dia del viaje al que corresponde la fecha cargada (la elegida o la que puso
        // la IA al leer el recibo). Si no coincide con ninguno, se avisa antes de enviar.
        const diaDelViaje = planComidas.find(d => d.fecha === nuevaFactura.fecha);
        // La fecha que leyo la IA no es el dia elegido: se avisa, pero no frena la carga.
        const fechaNoCoincide = esTipoComida(tabFactura) && !!nuevaFactura.fecha
            && !!nuevaFactura.fechaLeidaIA && nuevaFactura.fechaLeidaIA !== nuevaFactura.fecha;

        // Estimacion en dolares con la tasa del dia de la factura, solo para avisar
        // del tope antes de subirla. La conversion que vale la hace el backend.
        const tasaDelDia = tasasPorFecha[nuevaFactura.fecha];
        const montoNumerico = parseFloat(String(nuevaFactura.monto).replace(',', '.'));
        const montoUsdEstimado = (tasaDelDia && !isNaN(montoNumerico) && montoNumerico > 0)
            ? montoNumerico / tasaDelDia
            : null;
        const excedeTope = esTipoComida(tabFactura) && montoUsdEstimado != null
            && montoUsdEstimado > TOPES_COMIDA[tabFactura];

        return (
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-4 mb-6">
                    <button
                        onClick={() => { solicitudAbiertaRef.current = null; setViewMode('list'); setSelectedSolicitud(null); setFacturas([]); fetchViaticos(); }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft size={24} className="text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Legalizacion de Gastos</h1>
                        <p className="text-gray-500">Solicitud {selectedSolicitud.codigo} - {selectedSolicitud.destino}</p>
                    </div>
                </div>

                {selectedSolicitud.legalizacion_devuelta && (
                    <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3">
                        <AlertCircle size={20} className="text-amber-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold text-amber-800">CxP devolvió tu legalización</p>
                            <p className="text-sm text-amber-700 mt-0.5">{selectedSolicitud.legalizacion_devuelta.motivo}</p>
                            <p className="text-xs text-amber-600 mt-1">Corrige lo indicado y vuelve a enviar la legalización.</p>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-12 gap-8">
                    {/* Panel Izquierdo: Resumen de Cuenta */}
                    <div className="col-span-4">
                        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm sticky top-6">
                            <h3 className="font-bold text-gray-900 text-lg mb-6">{esTarjeta ? 'Gastos con Tarjeta' : 'Resumen de Cuenta'}</h3>

                            {esTarjeta ? (
                                <div className="space-y-5">
                                    <div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500">Total Facturas</span>
                                            <span className="font-bold text-gray-900 text-2xl">Bs. {formatoBs(totalGastos)}</span>
                                        </div>
                                    </div>
                                    <p className="text-sm bg-[#fdf6e3] border border-[#e6cf7a] rounded-xl p-3" style={{ color: '#8a6d1a' }}>
                                        Gasto pagado con tarjeta gerencial. No lleva reintegro: sube las facturas y ciérralo para dejarlo reportado.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-5">
                                    <div className="flex justify-between items-center">
                                        <span className="text-gray-500">Anticipo aprobado</span>
                                        <span className="font-semibold text-gray-900 text-lg">{usdFmt(montoReferencia)}</span>
                                    </div>
                                    {!sinAbono && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500">Abonado por CxP</span>
                                            <span className="font-semibold text-gray-900 text-lg">Bs. {formatoBs(selectedSolicitud.monto_bs)}</span>
                                        </div>
                                    )}
                                    <div>
                                        <div className="flex justify-between items-center gap-3">
                                            <span className="text-gray-500 leading-snug">Total reconocido por facturas</span>
                                            <span className="font-bold text-red-500 text-lg whitespace-nowrap shrink-0">-Bs. {formatoBs(reconocidoBs)}</span>
                                        </div>
                                        <p className="text-xs text-gray-400 text-right mt-1">
                                            Convertidas con la tasa del dia de cada factura
                                        </p>
                                    </div>
                                    {totalExcedidoUsd > 0 && (
                                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-amber-800">Excedido del tope</span>
                                                <span className="font-bold text-amber-700">Bs. {formatoBs(excedidoBs)}</span>
                                            </div>
                                            <p className="text-xs text-amber-700/80 mt-1">
                                                Gastaste de mas sobre el tope diario de las comidas. No se descuenta del
                                                anticipo: lo asume el empleado.
                                            </p>
                                        </div>
                                    )}
                                    <div className="pt-5 border-t-2 border-gray-100">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-gray-900 text-lg">Saldo Final</span>
                                            <span className={`font-bold text-2xl ${saldoDir >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                                {saldoBs != null
                                                    ? `Bs. ${formatoBs(Math.abs(saldoBs))}`
                                                    : usdFmt(Math.abs(saldo))}
                                            </span>
                                        </div>
                                        <p className="text-sm text-right text-gray-400 mt-2">
                                            {saldoDir >= 0 ? 'A favor de SURA (Reintegro)' : 'A favor del Empleado'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Datos Bancarios y Comprobante de Pago para Reintegro */}
                            {!esTarjeta && requiereReintegro && (
                                <div className="mt-8 space-y-5">
                                    <div className="p-5 bg-amber-50 border border-amber-200 rounded-2xl">
                                        <div className="flex items-center gap-2 text-xs font-bold text-amber-700 uppercase mb-3">
                                            <CreditCard size={14} /> Datos para tu Reintegro
                                        </div>
                                        <p className="text-sm font-medium text-gray-900">Banco: {selectedSolicitud.banco?.banco}</p>
                                        <p className="text-sm text-gray-600 mt-1">RIF: {selectedSolicitud.banco?.rif}</p>
                                        <p className="text-sm text-gray-600 mt-1">Tel: {selectedSolicitud.banco?.telefono}</p>
                                    </div>

                                    <div className="p-5 bg-gray-50 rounded-2xl">
                                        <div className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase mb-3">
                                            <Upload size={14} /> Comprobante de Pago Movil
                                        </div>
                                        <p className="text-sm text-gray-500 mb-2">
                                            Sube la captura o el PDF de tu transferencia/pago movil del reintegro. La IA lo lee y verifica el monto.
                                        </p>
                                        {yaReintegradoBs > 0 && pendienteBs != null && (
                                            <div className="mb-3 p-3 rounded-xl border border-red-200 bg-red-50 text-xs text-red-800">
                                                Ya registraste Bs. {formatoBs(yaReintegradoBs)} de Bs. {formatoBs(saldoBs)}.
                                                La solicitud sigue en Pagado hasta que reintegres el resto.
                                            </div>
                                        )}
                                        {pendienteBs != null && pendienteBs > 1 && (
                                            <p className="text-sm font-semibold text-gray-900 mb-4">
                                                {yaReintegradoBs > 0 ? 'Te falta reintegrar' : 'Debes reintegrar'}: Bs. {formatoBs(pendienteBs)}
                                            </p>
                                        )}

                                        {comprobantePagoPreview ? (
                                            <div className="relative">
                                                {esArchivoPdf(comprobantePago)
                                                    ? <TilePdf nombre={comprobantePago?.name} className="w-full h-40 rounded-xl" />
                                                    : (
                                                        <img
                                                            src={comprobantePagoPreview}
                                                            alt="Comprobante"
                                                            className="w-full h-40 object-cover rounded-xl cursor-zoom-in"
                                                            onClick={() => setPreviewImg(comprobantePagoPreview)}
                                                        />
                                                    )}
                                                <button
                                                    onClick={removeComprobantePago}
                                                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                                                <ImagePlus size={28} className="text-gray-400 mb-2" />
                                                <span className="text-sm text-gray-500">Subir imagen o PDF</span>
                                                <input
                                                    type="file"
                                                    accept="image/*,application/pdf"
                                                    className="hidden"
                                                    onChange={handleComprobantePago}
                                                />
                                            </label>
                                        )}

                                        {comprobantePago && analisisReintegro.estado !== 'idle' && (() => {
                                            const a = analisisReintegro.datos;
                                            if (analisisReintegro.estado === 'cargando') {
                                                return (
                                                    <div className="mt-3 flex items-center gap-2 text-xs text-violet-700 bg-violet-50 border border-violet-200 rounded-xl p-3">
                                                        <Loader2 size={14} className="animate-spin" /> Leyendo el comprobante con IA...
                                                    </div>
                                                );
                                            }
                                            if (analisisReintegro.estado === 'error') {
                                                return (
                                                    <div className="mt-3 flex items-start gap-2 text-xs text-gray-600 bg-white border border-gray-200 rounded-xl p-3">
                                                        <AlertCircle size={14} className="shrink-0 mt-0.5" />
                                                        <span>{analisisReintegro.error} Sin la lectura del monto no se puede legalizar: sube un archivo más nítido.</span>
                                                    </div>
                                                );
                                            }
                                            const ok = a.veredicto === 'coincide';
                                            const grave = ['incompleto', 'fallido', 'no_es_comprobante', 'repetido'].includes(a.veredicto);
                                            const clase = ok
                                                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                                : grave ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800';
                                            return (
                                                <div className={`mt-3 p-3 rounded-xl border text-xs ${clase}`}>
                                                    <div className="flex items-start gap-2">
                                                        {ok ? <CheckCircle size={15} className="shrink-0 mt-0.5" /> : <AlertTriangle size={15} className="shrink-0 mt-0.5" />}
                                                        <p className="font-semibold">{a.mensaje}</p>
                                                    </div>
                                                    {a.veredicto === 'incompleto' && (
                                                        <p className="mt-2">
                                                            Si lo envías así, se registra un pago de Bs. {formatoBs(a.monto_detectado)} y la
                                                            solicitud sigue en Pagado hasta que reintegres el resto. Si ese no es el monto
                                                            de tu comprobante, no lo envíes: quítalo y sube una imagen más nítida.
                                                        </p>
                                                    )}
                                                    {(a.monto_detectado != null || a.referencia || a.fecha) && (
                                                        <p className="mt-2 opacity-80">
                                                            {a.monto_detectado != null && <>Pagado: Bs. {formatoBs(a.monto_detectado)} · </>}
                                                            Debes: Bs. {formatoBs(a.esperado_bs)}
                                                            {a.referencia ? ` · Ref. ${a.referencia}` : ''}
                                                            {a.fecha ? ` · ${fechaCorta(a.fecha)}` : ''}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}

                            {!esTarjeta && sinAbono && (
                                <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                    <p className="text-sm text-amber-700 font-medium">CxP aún no ha cargado el monto abonado. No es posible legalizar hasta que se registre el pago.</p>
                                </div>
                            )}

                            {esTarjeta && facturas.length === 0 && (
                                <div className="mt-8 p-4 bg-[#fdf6e3] border border-[#e6cf7a] rounded-xl">
                                    <p className="text-sm font-medium" style={{ color: '#8a6d1a' }}>Sube al menos una factura para poder cerrar y reportar el gasto.</p>
                                </div>
                            )}

                            <button
                                onClick={handleLegalizar}
                                disabled={submitting || (esTarjeta ? facturas.length === 0 : (sinAbono || (requiereReintegro && !comprobantePago)))}
                                className="w-full mt-8 py-4 bg-gray-900 text-white rounded-xl font-medium text-base shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {submitting ? (
                                    <RefreshCw size={20} className="animate-spin" />
                                ) : (
                                    <>
                                        <Send size={18} />
                                        {esTarjeta ? 'Cerrar y Reportar' : (requiereReintegro ? 'Enviar con Comprobante' : 'Cerrar y Enviar')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Panel Derecho: Cargar Facturas */}
                    {/* flex-col + flex-1 en la lista: la columna estira hasta igualar
                        la altura del resumen de la izquierda, sin dejar hueco abajo. */}
                    <div className="col-span-8 flex flex-col">
                        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm mb-6">
                            <h3 className="font-bold text-gray-900 text-lg mb-2 flex items-center gap-2">
                                <Receipt size={22} className="text-blue-500" /> Cargar Facturas
                            </h3>
                            <p className="text-sm text-gray-500 mb-5">
                                Sube la foto de cada factura — la IA puede leer los datos automáticamente
                            </p>

                            {(() => {
                                const comidasVisibles = tabsVisibles.filter(t => esTipoComida(t.id));
                                const otrasVisibles = tabsVisibles.filter(t => !esTipoComida(t.id));
                                const cargadaDe = (tipo, fecha) => facturas.find(f => (f.tipo || 'otros') === tipo && f.fecha === fecha);

                                const elegirComida = (tipo, dia) => {
                                    setErrorFactura('');
                                    setTabFactura(tipo);
                                    setSeleccionHecha(true);
                                    setNuevaFactura(prev => ({ ...prev, fecha: dia.fecha }));
                                };

                                // Desplegar/plegar un dia. Si la comida elegida era de otro dia, se suelta:
                                // el formulario siempre carga la del dia que esta abierto.
                                const abrirDia = (dia) => {
                                    const cerrar = diaAbierto === dia.fecha;
                                    setDiaAbierto(cerrar ? null : dia.fecha);
                                    setErrorFactura('');
                                    if (esTipoComida(tabFactura) && nuevaFactura.fecha && (cerrar || nuevaFactura.fecha !== dia.fecha)) {
                                        setNuevaFactura(prev => ({ ...prev, fecha: '' }));
                                        setSeleccionHecha(false);
                                    }
                                };

                                return (
                                    <>
                                        {/* Comidas: se toca el dia, se despliega y ahi se elige que comida cargar.
                                            Cada comida se carga maximo una vez por dia, y en total hasta lo aprobado,
                                            en los dias que la persona quiera. */}
                                        {comidasVisibles.length > 0 && (
                                            <div className="mb-5">
                                                <div className="flex items-baseline justify-between mb-2">
                                                    <label className="text-sm font-medium text-gray-600">Comidas del viaje</label>
                                                    <span className="text-xs text-gray-400">Toca un día para cargar sus comidas</span>
                                                </div>
                                                <p className="text-xs text-gray-600 mb-1">
                                                    Máximo por día:{' '}
                                                    {comidasVisibles.map((t, i, arr) => (
                                                        <React.Fragment key={t.id}>
                                                            <strong>{t.label} ${TOPES_COMIDA[t.id]}</strong>{i < arr.length - 1 ? ' · ' : ''}
                                                        </React.Fragment>
                                                    ))}
                                                    . Una de cada comida por día.
                                                </p>
                                                <p className="text-xs text-gray-500 mb-3">
                                                    Por cargar:{' '}
                                                    {comidasVisibles.map((t, i, arr) => {
                                                        const total = aprobadasDe(t.id);
                                                        const usadas = facturas.filter(f => f.tipo === t.id).length;
                                                        return (
                                                            <React.Fragment key={t.id}>
                                                                <span className={usadas >= total ? 'text-emerald-600' : ''}>
                                                                    {t.label} {usadas}/{total}
                                                                </span>
                                                                {i < arr.length - 1 ? ' · ' : ''}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </p>

                                                {planComidas.length === 0 ? (
                                                    <p className="text-sm text-gray-400">La solicitud no tiene fechas de viaje.</p>
                                                ) : (
                                                    <div
                                                        className="grid gap-2 items-start"
                                                        style={{ gridTemplateColumns: `repeat(${Math.min(planComidas.length, 5)}, minmax(0, 1fr))` }}
                                                    >
                                                        {planComidas.map((dia) => {
                                                            const { semana, numero, mes } = partesFecha(dia.fecha);
                                                            const comidas = comidasVisibles.filter(t => dia[t.id]);
                                                            const cargadasDia = comidas.filter(t => cargadaDe(t.id, dia.fecha));
                                                            const abierto = diaAbierto === dia.fecha;
                                                            return (
                                                                <div
                                                                    key={dia.fecha}
                                                                    className={`rounded-xl border transition-colors ${abierto
                                                                        ? 'border-blue-300 bg-blue-50/40 ring-1 ring-blue-200'
                                                                        : 'border-gray-200 bg-white hover:border-blue-300'}`}
                                                                >
                                                                    <button type="button" onClick={() => abrirDia(dia)} className="w-full p-3 text-left">
                                                                        <div className="flex items-start justify-between">
                                                                            <div>
                                                                                <p className="text-[11px] font-medium text-gray-400 uppercase leading-none">{semana}</p>
                                                                                <p className="text-lg font-bold text-gray-900 leading-tight">
                                                                                    {numero} <span className="text-xs font-medium text-gray-400">{mes}</span>
                                                                                </p>
                                                                            </div>
                                                                            <span className="text-[11px] font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">Día {dia.dia}</span>
                                                                        </div>
                                                                        <div className="flex items-center gap-1 mt-2 min-h-[22px]">
                                                                            {cargadasDia.length > 0 ? (
                                                                                <span className="flex flex-wrap gap-1">
                                                                                    {cargadasDia.map(t => {
                                                                                        const Icono = t.icon;
                                                                                        return (
                                                                                            <span key={t.id} className="flex items-center gap-1 text-[11px] font-medium text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">
                                                                                                <Icono size={11} /> {t.label}
                                                                                            </span>
                                                                                        );
                                                                                    })}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-[11px] text-gray-400">{abierto ? 'Elige la comida' : 'Sin facturas'}</span>
                                                                            )}
                                                                            <ChevronDown size={15} className={`ml-auto shrink-0 text-gray-400 transition-transform ${abierto ? 'rotate-180' : ''}`} />
                                                                        </div>
                                                                    </button>

                                                                    {abierto && (
                                                                        <div className="px-3 pb-3 pt-2.5 space-y-1.5 border-t border-blue-100">
                                                                            {comidas.map((t) => {
                                                                                const Icono = t.icon;
                                                                                const cargada = cargadaDe(t.id, dia.fecha);
                                                                                if (cargada) {
                                                                                    const verImagen = cargada.imagen && !esPdf(cargada.imagen);
                                                                                    return (
                                                                                        <div key={t.id} className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-medium bg-emerald-50 border border-emerald-100 text-emerald-700">
                                                                                            <Icono size={14} className="shrink-0" />
                                                                                            <span className="truncate">{t.label}</span>
                                                                                            {cargada.imagen_2 && <span className="text-[10px] font-normal text-emerald-600 shrink-0">2 recibos</span>}
                                                                                            <span className="ml-auto text-xs shrink-0">$ {parseFloat(cargada.monto_usd || 0).toFixed(2)}</span>
                                                                                            {cargada.imagen && esPdf(cargada.imagen) && (
                                                                                                <EnlacePdf ruta={cargada.imagen} className="p-1 rounded-md hover:bg-emerald-100">
                                                                                                    <Eye size={14} />
                                                                                                </EnlacePdf>
                                                                                            )}
                                                                                            {verImagen && (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => { setPreviewFactura(cargada); setPreviewImg(`/api${cargada.imagen}`); }}
                                                                                                    title="Ver factura"
                                                                                                    className="shrink-0 p-1 rounded-md hover:bg-emerald-100"
                                                                                                >
                                                                                                    <Eye size={14} />
                                                                                                </button>
                                                                                            )}
                                                                                            {cargada.imagen_2 && (esPdf(cargada.imagen_2) ? (
                                                                                                <EnlacePdf ruta={cargada.imagen_2} className="p-1 rounded-md hover:bg-emerald-100">
                                                                                                    <Eye size={14} />
                                                                                                </EnlacePdf>
                                                                                            ) : (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => { setPreviewFactura(cargada); setPreviewImg(`/api${cargada.imagen_2}`); }}
                                                                                                    title="Ver el segundo recibo"
                                                                                                    className="shrink-0 p-1 rounded-md hover:bg-emerald-100"
                                                                                                >
                                                                                                    <Eye size={14} />
                                                                                                </button>
                                                                                            ))}
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => setConfirmacion({
                                                                                                    titulo: 'Quitar factura',
                                                                                                    mensaje: `¿Quitar la factura del ${t.id} del ${fechaCorta(dia.fecha)}? Podrás subirla de nuevo.`,
                                                                                                    etiqueta: 'Quitar',
                                                                                                    // Al quitarla queda lista para subir la correcta en esa misma comida.
                                                                                                    accion: async () => { if (await removeFactura(cargada.id)) elegirComida(t.id, dia); },
                                                                                                })}
                                                                                                title="Quitar factura"
                                                                                                className="shrink-0 p-1 rounded-md text-emerald-600 hover:bg-red-50 hover:text-red-500"
                                                                                            >
                                                                                                <X size={14} />
                                                                                            </button>
                                                                                        </div>
                                                                                    );
                                                                                }
                                                                                const sinCupo = cupoLlenoDe(t.id);
                                                                                const activa = tabFactura === t.id && dia.fecha === nuevaFactura.fecha;
                                                                                return (
                                                                                    <button
                                                                                        key={t.id}
                                                                                        type="button"
                                                                                        disabled={sinCupo}
                                                                                        onClick={() => elegirComida(t.id, dia)}
                                                                                        title={sinCupo ? `Ya cargaste todos los ${t.id}s del anticipo` : `Cargar ${t.id} del ${fechaCorta(dia.fecha)}`}
                                                                                        className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                                                                                            sinCupo
                                                                                                ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                                                                                                : activa
                                                                                                    ? 'bg-blue-600 border-blue-600 text-white'
                                                                                                    : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50'
                                                                                        }`}
                                                                                    >
                                                                                        <Icono size={14} className="shrink-0" />
                                                                                        <span className="truncate">{t.label}</span>
                                                                                        <span className={`ml-auto text-[11px] font-normal shrink-0 ${activa ? 'text-blue-100' : 'text-gray-400'}`}>
                                                                                            {sinCupo ? 'Sin cupo' : `Máx. $${TOPES_COMIDA[t.id]}`}
                                                                                        </span>
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Hospedaje, traslado y otros: no van por dia, se eligen aparte */}
                                        {otrasVisibles.length > 0 && (
                                            <div className="mb-5">
                                                <label className="text-sm font-medium text-gray-600 block mb-2">Otros gastos del viaje</label>
                                                <div className="flex flex-wrap gap-2">
                                                    {otrasVisibles.map(({ id, label, icon: Icon }) => {
                                                        const activa = seleccionHecha && tabFactura === id;
                                                        const cargadas = facturas.filter(f => (f.tipo || 'otros') === id).length;
                                                        return (
                                                            <button
                                                                key={id}
                                                                type="button"
                                                                onClick={() => {
                                                                    setErrorFactura('');
                                                                    if (activa) {
                                                                        setSeleccionHecha(false);
                                                                        return;
                                                                    }
                                                                    setTabFactura(id);
                                                                    setSeleccionHecha(true);
                                                                    setNuevaFactura(prev => ({ ...prev, fecha: '' }));
                                                                }}
                                                                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-medium border transition-colors ${activa
                                                                    ? 'bg-blue-600 border-blue-600 text-white'
                                                                    : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50'}`}
                                                            >
                                                                <Icon size={15} />
                                                                {label}
                                                                {cargadas > 0 && (
                                                                    <span className={`text-xs ${activa ? 'text-blue-100' : 'text-gray-400'}`}>({cargadas})</span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                );
                            })()}

                            {!mostrarFormulario && (
                                <div className="pt-5 border-t border-gray-100 text-center text-sm text-gray-400">
                                    Elige un día y la comida, o un gasto, para cargar su factura.
                                </div>
                            )}

                            {/* Formulario: foto a la izquierda, datos a la derecha */}
                            {mostrarFormulario && (() => {
                                const info = TIPOS_FACTURA.find(t => t.id === tabFactura);
                                const IconoCarga = info?.icon || Receipt;
                                const esComida = esTipoComida(tabFactura);
                                const pf = esComida && nuevaFactura.fecha ? partesFecha(nuevaFactura.fecha) : null;
                                const diaCarga = esComida ? planComidas.find(d => d.fecha === nuevaFactura.fecha) : null;
                                return (
                            <div className="pt-5 border-t border-gray-100">
                                <div className="flex items-center gap-3 mb-4 p-3.5 rounded-xl bg-blue-50 border border-blue-100">
                                    <div className="p-2 bg-white rounded-lg text-blue-600 shrink-0">
                                        <IconoCarga size={18} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-500">Estás cargando</p>
                                        <p className="text-base font-bold text-gray-900 leading-tight">
                                            {info?.label}
                                            {pf && <> del {pf.semana} {pf.numero} {pf.mes}</>}
                                            {diaCarga && <span className="text-sm font-medium text-gray-500"> · Día {diaCarga.dia} del viaje</span>}
                                        </p>
                                        {esComida && (
                                            <p className="text-xs text-gray-500 mt-0.5">Máximo ${TOPES_COMIDA[tabFactura]} para esta comida</p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSeleccionHecha(false);
                                            setErrorFactura('');
                                            setNuevaFactura({
                                                fecha: '', concepto: '', monto: '', detalle: '', imagen: null, imagenPreview: null, imagen2: null, imagenPreview2: null, recibosIA: null,
                                                razonSocial: '', rif: '', rifValido: null, fechaLeidaIA: '', lecturaToken: '',
                                            });
                                        }}
                                        className="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white"
                                        title="Cancelar"
                                        aria-label="Cancelar"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            <div className="grid grid-cols-1 md:grid-cols-[176px_1fr] gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-600 block mb-2">Foto o PDF</label>
                                    {nuevaFactura.imagenPreview ? (
                                        <div className="relative">
                                            {esArchivoPdf(nuevaFactura.imagen)
                                                ? <TilePdf nombre={nuevaFactura.imagen?.name} className="w-full h-[132px] rounded-xl" />
                                                : (
                                                    <img
                                                        src={nuevaFactura.imagenPreview}
                                                        alt="Factura"
                                                        className="w-full h-[132px] rounded-xl object-cover border border-gray-200 cursor-zoom-in"
                                                        onClick={() => setPreviewImg(nuevaFactura.imagenPreview)}
                                                    />
                                                )}
                                            <button
                                                type="button"
                                                onClick={() => setNuevaFactura(prev => ({
                                                    ...prev,
                                                    imagen: prev.imagen2 || null, imagenPreview: prev.imagenPreview2 || null,
                                                    imagen2: null, imagenPreview2: null,
                                                    fechaLeidaIA: '', lecturaToken: '', recibosIA: null, esFactura: null, avisoNoFactura: '',
                                                }))}
                                                className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full shadow"
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="flex flex-col items-center justify-center gap-1.5 w-full h-[132px] bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:bg-gray-100 hover:border-blue-300 transition-colors">
                                            <ImagePlus size={22} className="text-gray-400" />
                                            <span className="text-xs text-gray-500 text-center px-2">Subir foto o PDF</span>
                                            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleImagenFactura} />
                                        </label>
                                    )}
                                    {/* Comidas: hasta dos recibos (ej. la comida en un sitio y la bebida en otro) */}
                                    {esTipoComida(tabFactura) && nuevaFactura.imagen && (
                                        nuevaFactura.imagenPreview2 ? (
                                            <div className="relative mt-2">
                                                {esArchivoPdf(nuevaFactura.imagen2)
                                                    ? <TilePdf nombre={nuevaFactura.imagen2?.name} className="w-full h-[84px] rounded-xl" />
                                                    : (
                                                        <img
                                                            src={nuevaFactura.imagenPreview2}
                                                            alt="Segundo recibo"
                                                            className="w-full h-[84px] rounded-xl object-cover border border-gray-200 cursor-zoom-in"
                                                            onClick={() => setPreviewImg(nuevaFactura.imagenPreview2)}
                                                        />
                                                    )}
                                                <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 bg-white/90 rounded text-[10px] font-semibold text-gray-600">2º recibo</span>
                                                <button
                                                    type="button"
                                                    onClick={() => setNuevaFactura(prev => ({ ...prev, imagen2: null, imagenPreview2: null, fechaLeidaIA: '', lecturaToken: '', recibosIA: null, esFactura: null, avisoNoFactura: '' }))}
                                                    className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full shadow"
                                                    aria-label="Quitar el segundo recibo"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="mt-2 flex items-center justify-center gap-1.5 w-full py-2 border border-dashed border-gray-300 rounded-xl text-xs text-gray-500 cursor-pointer hover:bg-gray-50 hover:border-blue-300 transition-colors">
                                                <ImagePlus size={14} className="text-gray-400" />
                                                Agregar otro recibo de esta comida
                                                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleImagenFactura2} />
                                            </label>
                                        )
                                    )}
                                    {nuevaFactura.imagen && (
                                        <button
                                            type="button"
                                            onClick={analizarConIA}
                                            disabled={analizandoIA}
                                            className="mt-2 w-full py-2 bg-violet-50 text-violet-700 border border-violet-200 rounded-xl font-medium text-xs hover:bg-violet-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                                        >
                                            {analizandoIA ? <Loader2 size={13} className="animate-spin" /> : <FaMagic size={12} />}
                                            {analizandoIA ? 'Analizando...' : nuevaFactura.imagen2 ? 'Leer los 2 con IA' : 'Leer con IA'}
                                        </button>
                                    )}
                                </div>

                                <div className="space-y-3">
                                    {!esTipoComida(tabFactura) ? (
                                        <div>
                                            <label className="text-sm font-medium text-gray-600 block mb-1.5">Fecha</label>
                                            <input
                                                type="date"
                                                value={nuevaFactura.fecha}
                                                onChange={e => setNuevaFactura({ ...nuevaFactura, fecha: e.target.value })}
                                                className="w-full p-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm"
                                            />
                                        </div>
                                    ) : (
                                        // Solo para verificar: es la fecha que se leyo del recibo (o el dia
                                        // elegido arriba), y es la que decide la tasa de conversion.
                                        <div>
                                            <label className="text-sm font-medium text-gray-600 block mb-1.5">
                                                Fecha de la factura
                                            </label>
                                            <div className={`w-full p-2.5 rounded-xl border text-sm flex items-center justify-between gap-2 ${nuevaFactura.fecha
                                                ? diaDelViaje
                                                    ? 'bg-blue-50 border-blue-200 text-gray-900 font-medium'
                                                    : 'bg-amber-50 border-amber-200 text-amber-800 font-medium'
                                                : 'bg-gray-50 border-gray-200 text-gray-400'}`}>
                                                <span>{nuevaFactura.fecha ? fechaCorta(nuevaFactura.fecha) : 'dd/mm/aaaa'}</span>
                                                {nuevaFactura.fecha && (
                                                    <span className="text-[11px] font-normal opacity-70">
                                                        {diaDelViaje ? `Dia ${diaDelViaje.dia} del viaje` : 'Fuera del viaje'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <label className="text-sm font-medium text-gray-600 block mb-1.5">Concepto</label>
                                        <input
                                            type="text"
                                            value={nuevaFactura.concepto}
                                            onChange={e => setNuevaFactura({ ...nuevaFactura, concepto: e.target.value })}
                                            className="w-full p-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm"
                                            placeholder="Ej: Almuerzo en restaurante"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-gray-600 block mb-1.5">Monto (Bs)</label>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={nuevaFactura.monto}
                                            onChange={e => setNuevaFactura({ ...nuevaFactura, monto: e.target.value })}
                                            className="w-full p-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm font-semibold"
                                            placeholder="0,00"
                                        />
                                        {nuevaFactura.recibosIA?.length > 1 && (
                                            <div className="mt-1.5 space-y-0.5">
                                                {nuevaFactura.recibosIA.map((r, i) => (
                                                    <p key={i} className={`text-[11px] ${r.rif_valido && r.es_factura !== false ? 'text-gray-500' : 'text-amber-700'}`}>
                                                        Recibo {i + 1}: {r.concepto || '—'} · Bs {r.monto != null ? formatoBs(r.monto) : '?'}
                                                        {r.fecha ? ` · ${fechaCorta(r.fecha)}` : ''}
                                                        {r.es_factura === false ? ' · no es una factura (comprobante de pago)'
                                                            : !r.rif_valido ? ' · no está a nombre de SURA' : ''}
                                                    </p>
                                                ))}
                                                <p className="text-[11px] font-medium text-gray-600">Se cargan como una sola factura con el monto sumado.</p>
                                            </div>
                                        )}
                                    </div>
                                    {/* Datos fiscales que leyo la IA. La factura debe estar
                                        emitida a la empresa; si no cuadra, se carga igual
                                        con el aviso para CxP (la IA lee mal tickets borrosos). */}
                                    {nuevaFactura.esFactura !== false && (nuevaFactura.rif || nuevaFactura.razonSocial || nuevaFactura.lecturaToken) && (
                                        <div className={`p-3 rounded-xl border text-xs ${nuevaFactura.rifValido
                                            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                            : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                                            <div className="flex items-start gap-2">
                                                {nuevaFactura.rifValido
                                                    ? <CheckCircle size={15} className="shrink-0 mt-0.5" />
                                                    : <AlertCircle size={15} className="shrink-0 mt-0.5" />}
                                                <div className="min-w-0">
                                                    <p className="font-medium truncate">
                                                        {nuevaFactura.razonSocial || 'Sin razon social'}
                                                    </p>
                                                    <p className="opacity-80">RIF {nuevaFactura.rif || 'no legible'}</p>
                                                    {!nuevaFactura.rifValido && (
                                                        <p className="mt-1">
                                                            La IA no la ve a nombre de SURA de Venezuela, C.A.
                                                            (J-30622535-8). En los tickets termicos o borrosos el RIF
                                                            se lee mal con frecuencia: si la factura si es de la
                                                            empresa, cargala igual y CxP lo revisara.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Tope diario de la comida: lo que se pase no se reconoce */}
                                    {esTipoComida(tabFactura) && montoUsdEstimado != null && (
                                        <div className={`p-3 rounded-xl border text-xs ${excedeTope
                                            ? 'bg-amber-50 border-amber-200 text-amber-800'
                                            : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                                            <div className="flex items-start gap-2">
                                                <AlertCircle size={15} className={`shrink-0 mt-0.5 ${excedeTope ? '' : 'opacity-40'}`} />
                                                <span>
                                                    Equivale a <strong>${montoUsdEstimado.toFixed(2)}</strong> y el tope
                                                    del {tabFactura} es <strong>${TOPES_COMIDA[tabFactura]}</strong> por dia.
                                                    {excedeTope && (
                                                        <> Se reconoceran <strong>${TOPES_COMIDA[tabFactura]}.00</strong> y
                                                        los <strong>${(montoUsdEstimado - TOPES_COMIDA[tabFactura]).toFixed(2)}</strong> restantes
                                                        los asume el empleado. El tope es por dia y no se comparte entre dias.</>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Aviso antes de intentar: la IA pone la fecha que dice
                                        el recibo, y puede no ser un dia del viaje. */}
                                    {esTipoComida(tabFactura) && nuevaFactura.fecha && !diaDelViaje && (
                                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                                            <AlertCircle size={15} className="shrink-0 mt-0.5" />
                                            <span>
                                                La factura es del <strong>{fechaCorta(nuevaFactura.fecha)}</strong>, que no es un dia del viaje
                                                {selectedSolicitud.fecha_inicio && selectedSolicitud.fecha_fin
                                                    ? ` (del ${fechaCorta(selectedSolicitud.fecha_inicio)} al ${fechaCorta(selectedSolicitud.fecha_fin)})`
                                                    : ''}.
                                                Revisa que sea el recibo correcto: mas adelante no se va a poder cargar asi.
                                            </span>
                                        </div>
                                    )}

                                    {fechaNoCoincide && (
                                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                                            <AlertCircle size={15} className="shrink-0 mt-0.5" />
                                            <span>
                                                La IA leyó en la factura el <strong>{fechaCorta(nuevaFactura.fechaLeidaIA)}</strong> y estás
                                                cargando el {tabFactura} del <strong>{fechaCorta(nuevaFactura.fecha)}</strong>. Si la IA leyó
                                                mal la fecha, cárgala igual y CxP lo revisará.
                                            </span>
                                        </div>
                                    )}

                                    {esTipoComida(tabFactura) && nuevaFactura.lecturaToken && !nuevaFactura.fechaLeidaIA && (
                                        <p className="text-xs text-gray-500">
                                            La IA no pudo leer la fecha del recibo: se cargará con el día elegido y CxP la verá como fecha no verificada.
                                        </p>
                                    )}

                                    {nuevaFactura.esFactura === false && (
                                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                                            <AlertCircle size={15} className="shrink-0 mt-0.5" />
                                            <span>
                                                {nuevaFactura.avisoNoFactura || 'La IA no lo reconoce como factura.'}
                                                {' '}Si sí es la factura, cárgala igual y CxP lo revisará.
                                            </span>
                                        </div>
                                    )}

                                    {errorFactura && (
                                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
                                            <AlertCircle size={15} className="shrink-0 mt-0.5" />
                                            <span className="flex-1">{errorFactura}</span>
                                            <button
                                                type="button"
                                                onClick={() => setErrorFactura('')}
                                                className="shrink-0 text-red-400 hover:text-red-600"
                                                aria-label="Cerrar aviso"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={addFactura}
                                        disabled={!nuevaFactura.concepto || !nuevaFactura.monto || submitting || (esTipoComida(tabFactura) && !nuevaFactura.fecha) || !nuevaFactura.lecturaToken}
                                        className="w-full py-3 bg-blue-500 text-white rounded-xl font-medium text-sm hover:bg-blue-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {submitting ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
                                        {!nuevaFactura.lecturaToken
                                            ? 'Lee la factura con IA para cargarla'
                                            : esTipoComida(tabFactura) && !nuevaFactura.fecha
                                            ? 'Elige un día y la comida en el calendario'
                                            : esTipoComida(tabFactura)
                                                ? `Cargar ${tabFactura} del ${fechaCorta(nuevaFactura.fecha)}`
                                                : `Cargar factura de ${TIPOS_FACTURA.find(t => t.id === tabFactura)?.label.toLowerCase()}`}
                                    </button>
                                </div>
                            </div>
                            </div>
                                );
                            })()}
                        </div>

                        {/* Lista de comprobantes de la pestaña activa */}
                        <div className="space-y-3 flex-1 flex flex-col">
                            {facturasDelTab.map((factura) => (
                                <div key={factura.id} className="bg-white p-5 rounded-2xl border border-gray-100 flex items-center justify-between hover:shadow-sm transition-all">
                                    <div className="flex items-center gap-4">
                                        {factura.imagenPreview || factura.imagen ? (
                                            (esPdf(factura.imagen) || (factura.imagenPreview || '').startsWith('data:application/pdf')) ? (
                                                factura.imagen ? (
                                                    <EnlacePdf ruta={factura.imagen}>
                                                        <TilePdf className="w-14 h-14 rounded-xl" />
                                                    </EnlacePdf>
                                                ) : (
                                                    <TilePdf className="w-14 h-14 rounded-xl" />
                                                )
                                            ) : (
                                                <img
                                                    src={factura.imagenPreview || `/api${factura.imagen}`}
                                                    alt="Factura"
                                                    className="w-14 h-14 object-cover rounded-xl cursor-zoom-in"
                                                    onClick={() => setPreviewImg(factura.imagenPreview || `/api${factura.imagen}`)}
                                                />
                                            )
                                        ) : (
                                            <div className="p-3.5 bg-gray-50 rounded-xl text-gray-400">
                                                <Receipt size={22} />
                                            </div>
                                        )}
                                        {factura.imagen_2 && (
                                            <MiniaturaRecibo ruta={factura.imagen_2} onPreview={setPreviewImg} className="w-14 h-14 rounded-xl" etiqueta="2º" />
                                        )}
                                        <div>
                                            <h4 className="font-medium text-gray-900 text-base">{factura.concepto}</h4>
                                            <p className="text-sm text-gray-500">
                                                {factura.fecha ? fechaCorta(factura.fecha) : 'Sin fecha'}
                                                {factura.tasa ? (
                                                    <span className="text-gray-400">
                                                        {' · '}tasa {formatoTasa(factura.tasa)}
                                                        {factura.fecha_tasa && factura.fecha_tasa !== factura.fecha
                                                            ? ` del ${fechaCorta(factura.fecha_tasa)}`
                                                            : ''}
                                                    </span>
                                                ) : null}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-5">
                                        <div className="text-right">
                                            <span className="font-bold text-gray-900 text-lg block leading-tight">
                                                $ {parseFloat(factura.monto_usd_reconocido != null ? factura.monto_usd_reconocido : factura.monto_usd || 0).toFixed(2)}
                                            </span>
                                            <span className="text-xs text-gray-400">Bs {formatoBs(parseFloat(factura.monto))}</span>
                                            {factura.excedente_usd > 0 && (
                                                <span className="block text-xs text-amber-600 font-medium">
                                                    +${parseFloat(factura.excedente_usd).toFixed(2)} excedido
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => removeFactura(factura.id)}
                                            className="text-red-400 hover:text-red-600 p-1"
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </div>
                                </div>
                            ))}

                            {facturasDelTab.length === 0 && (
                                <div className="flex-1 flex flex-col items-center justify-center text-center py-16 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                                    <Receipt size={48} className="text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500 text-lg">
                                        Sin facturas de {TIPOS_FACTURA.find(t => t.id === tabFactura)?.label.toLowerCase()}
                                    </p>
                                    <p className="text-gray-400 text-sm mt-1">
                                        {facturas.length > 0
                                            ? `Tienes ${facturas.length} ${facturas.length === 1 ? 'factura cargada' : 'facturas cargadas'} en otras pestañas`
                                            : 'Agrega comprobantes de tus gastos del viaje'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================
    // RENDER PRINCIPAL
    // ============================================
    return (
        <div className="max-w-[1600px] mx-auto animate-fadeIn pb-12 pt-4">
            <AnimatePresence mode="wait">
                {viewMode === 'list' && (
                    <motion.div key="list" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                        {renderList()}
                    </motion.div>
                )}
                {viewMode === 'create' && (
                    <motion.div key="create" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                        {renderForm(false)}
                    </motion.div>
                )}
                {viewMode === 'edit' && (
                    <motion.div key="edit" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                        {renderForm(true)}
                    </motion.div>
                )}
                {viewMode === 'detail' && (
                    <motion.div key="detail" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                        {renderDetail()}
                    </motion.div>
                )}
                {viewMode === 'legalize' && (
                    <motion.div key="legalize" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                        {renderLegalize()}
                    </motion.div>
                )}
                {viewMode === 'gestion' && (
                    <motion.div key="gestion" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}>
                        <div className="max-w-[1400px] mx-auto">
                            <div className="flex items-center gap-4 mb-6">
                                <button onClick={() => setViewMode('list')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                    <ArrowLeft size={24} className="text-gray-600" />
                                </button>
                                <h1 className="text-2xl font-bold text-gray-900">Gestión de Viáticos — CxP</h1>
                                <button
                                    onClick={() => setShowTarjetasPanel(true)}
                                    className="ml-auto flex items-center gap-2 px-4 py-2.5 bg-[#fbf1d3] hover:bg-[#f5e7bd] text-[#8a6d1a] rounded-xl transition-colors font-medium text-sm"
                                >
                                    <CreditCard size={16} /> Tarjetas gerenciales
                                </button>
                            </div>

                            {/* Filtros */}
                            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit flex-wrap">
                                {[
                                    { key: 'pendiente', label: 'Por Aprobar' },
                                    { key: 'aprobado', label: 'Por Pagar' },
                                    { key: 'pagado', label: 'Pagados' },
                                    { key: 'legalizado', label: 'Legalizados' },
                                    { key: 'tarjeta_gerencial', label: 'Tarjeta gerencial' },
                                    { key: 'anulado', label: 'Anulados' },
                                ].map(tab => (
                                    <button
                                        key={tab.key}
                                        onClick={() => setGestionFiltro(tab.key)}
                                        className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${gestionFiltro === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                    >
                                        {tab.label}
                                        {tab.key === 'legalizado' && pendientesConfirmacion > 0 && (
                                            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700">
                                                {pendientesConfirmacion}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {gestionFiltro === 'legalizado' && (
                                <div className="flex items-center gap-2 mb-5">
                                    {[
                                        { key: 'pendientes', label: 'Pendientes por confirmar', cantidad: gestionSolicitudes.filter(s => s.legalizacion_estado === 'pendiente_cxp').length },
                                        { key: 'cerrados', label: 'Verificados y cerrados', cantidad: gestionSolicitudes.filter(s => s.legalizacion_estado !== 'pendiente_cxp').length },
                                    ].map(op => (
                                        <button
                                            key={op.key}
                                            onClick={() => setLegalizadosVista(op.key)}
                                            className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${legalizadosVista === op.key
                                                ? (op.key === 'pendientes' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-emerald-50 border-emerald-300 text-emerald-800')
                                                : 'bg-white border-gray-200 text-gray-500 hover:text-gray-700'}`}
                                        >
                                            {op.label} · {op.cantidad}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {(() => {
                                const visibles = gestionFiltro === 'legalizado'
                                    ? gestionSolicitudes.filter(s => (s.legalizacion_estado === 'pendiente_cxp') === (legalizadosVista === 'pendientes'))
                                    : gestionSolicitudes;
                                const vacio = gestionFiltro === 'legalizado'
                                    ? (legalizadosVista === 'pendientes' ? 'pendientes por confirmar' : 'legalizadas cerradas')
                                    : (({ aprobado: 'por pagar', pagado: 'pagadas', pendiente: 'por aprobar', tarjeta_gerencial: 'de tarjeta gerencial', anulado: 'anuladas' }[gestionFiltro]) || '');
                                return gestionLoading ? (
                                <div className="text-center py-16"><Loader2 size={32} className="animate-spin text-gray-400 mx-auto" /></div>
                            ) : visibles.length === 0 ? (
                                <div className="text-center py-16 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
                                    <Receipt size={48} className="text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500 text-lg">Sin solicitudes {vacio}</p>
                                </div>
                            ) : (
                                <div className="grid gap-4">
                                    {visibles.map(s => (
                                        <GestionCard
                                            key={s.id}
                                            solicitud={s}
                                            onConfirmarLegalizacion={(nota) => handleConfirmarLegalizacion(s, nota)}
                                            onDevolverLegalizacion={() => abrirDevolverModal(s)}
                                            onAjustarReintegro={(ajuste) => handleAjustarReintegro(s, ajuste)}
                                            onPagar={() => abrirPagoModal(s)}
                                            onAnular={() => { setAnularModal(s); setMotivoAnulacion(''); }}
                                            onAprobarCxp={() => { setAprobarCxpModal(s); setMotivoAprobacionCxp(''); }}
                                            onDownloadPdf={async () => {
                                                try {
                                                    const res = await axiosInstance.get(`/api/viaticos/${s.id}/pdf`, { responseType: 'blob' });
                                                    const url = window.URL.createObjectURL(new Blob([res.data]));
                                                    const a = document.createElement('a'); a.href = url; a.download = `Viatico_${s.codigo}.pdf`; a.click();
                                                    window.URL.revokeObjectURL(url);
                                                } catch (err) { console.error(err); }
                                            }}
                                        />
                                    ))}
                                </div>
                            );
                            })()}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {previewImg && createPortal((
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4 cursor-zoom-out"
                    onClick={() => { setPreviewImg(null); setPreviewFactura(null); }}
                >
                    <div className="flex flex-col items-center max-w-full max-h-full">
                        <img src={previewImg} alt="Vista previa" className={`max-w-[90vw] object-contain rounded-xl ${previewFactura ? 'max-h-[75vh]' : 'max-h-[85vh]'}`} />
                        {previewFactura && (
                            <div className="mt-3 bg-white rounded-xl px-4 py-2.5 text-sm text-gray-700 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
                                <span>Bs {formatoBs(parseFloat(previewFactura.monto))}</span>
                                <span className="font-semibold text-gray-900">$ {parseFloat(previewFactura.monto_usd || 0).toFixed(2)}</span>
                                {previewFactura.tasa ? (
                                    <span className="text-gray-500">
                                        Tasa {formatoTasa(previewFactura.tasa)} Bs/$
                                        {previewFactura.fecha_tasa || previewFactura.fecha
                                            ? ` · ${fechaCorta(previewFactura.fecha_tasa || previewFactura.fecha)}`
                                            : ''}
                                    </span>
                                ) : (
                                    <span className="text-gray-400">Sin tasa registrada</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ), document.body)}

            <ConfirmDialog
                isOpen={!!confirmacion}
                title={confirmacion?.titulo}
                message={confirmacion?.mensaje}
                confirmLabel={confirmacion?.etiqueta || 'Confirmar'}
                variant="danger"
                onConfirm={() => { const a = confirmacion?.accion; setConfirmacion(null); a?.(); }}
                onClose={() => setConfirmacion(null)}
            />

            {/* Modal de Pago CxP */}
            {pagoModal && createPortal((
                // Va en un portal a document.body: el contenedor de la vista usa
                // animate-fadeIn, que deja un transform permanente, y un transform
                // hace que los hijos position:fixed se midan contra ese contenedor
                // en vez de contra la pantalla (el modal quedaba encerrado ahi).
                // overflow-y-auto + items-start/my-auto: si la ventana es baja, el
                // modal se puede desplazar en vez de quedar cortado arriba.
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto p-4 flex items-start justify-center"
                    onMouseDown={e => { if (e.target === e.currentTarget) cerrarPagoModal(); }}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl my-auto overflow-hidden flex flex-col max-h-[calc(100vh-2rem)]"
                    >
                        <div className="flex items-start gap-4 px-6 pt-6 pb-5 shrink-0">
                            <div className="w-11 h-11 rounded-2xl bg-blue-50 flex items-center justify-center shrink-0">
                                <CreditCard size={20} className="text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="text-lg font-bold text-gray-900 leading-tight">Registrar pago</h2>
                                <p className="text-sm text-gray-500 truncate">{pagoModal.codigo} · {pagoModal.empleado_nombre}</p>
                            </div>
                            <button
                                onClick={cerrarPagoModal}
                                className="p-1.5 -mr-1 -mt-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
                                aria-label="Cerrar"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 items-start flex-1 min-h-0 overflow-y-auto">
                            <div className="space-y-5">
                            <div className="rounded-2xl border border-gray-100 bg-gray-50/70 divide-y divide-gray-100 text-sm">
                                <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                                    <span className="text-gray-500">Destino</span>
                                    <span className="font-medium text-gray-900 text-right truncate">{pagoModal.destino}</span>
                                </div>
                                <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                                    <span className="text-gray-500">Anticipo aprobado</span>
                                    <span className="font-bold text-gray-900">${pagoModal.monto_total?.toFixed(2)}</span>
                                </div>
                                <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                                    <span className="text-gray-500">Aprobado por</span>
                                    <span className="font-medium text-gray-900 text-right truncate">
                                        {pagoModal.aprobado_por_cxp_nombre
                                            ? `${pagoModal.aprobado_por_cxp_nombre} (CxP, en lugar de ${pagoModal.aprobador_nombre})`
                                            : pagoModal.aprobador_nombre}
                                    </span>
                                </div>
                                {pagoModal.motivo_aprobacion_cxp && (
                                    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
                                        <span className="text-gray-500 shrink-0">Motivo CxP</span>
                                        <span className="text-gray-700 text-right">{pagoModal.motivo_aprobacion_cxp}</span>
                                    </div>
                                )}
                                {pagoModal.fecha_aprobacion && (
                                    <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                                        <span className="text-gray-500">Fecha de aprobacion</span>
                                        <span className="font-medium text-gray-900">{new Date(pagoModal.fecha_aprobacion).toLocaleDateString('es-VE')}</span>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Fecha en la que se realiza el pago</label>
                                <input
                                    type="date"
                                    value={fechaPagoInput}
                                    onChange={e => setFechaPagoInput(e.target.value)}
                                    className="w-full px-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-blue-500 focus:bg-white transition-all text-base font-medium"
                                    autoFocus
                                />
                                <p className="text-xs text-gray-400 mt-1.5">La tasa se proyecta a partir de esta fecha; el monto en Bs lo calcula el sistema.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Captura del pago <span className="text-gray-400 font-normal">(opcional)</span>
                                </label>
                                {capturaPago ? (
                                    <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-2xl">
                                        {esArchivoPdf(capturaPago)
                                            ? <TilePdf nombre={capturaPago.name} className="w-14 h-14 rounded-xl shrink-0" />
                                            : (
                                                <img
                                                    src={capturaPagoPreview}
                                                    alt="Captura del pago"
                                                    className="w-14 h-14 rounded-xl object-cover border border-gray-200 shrink-0 cursor-zoom-in"
                                                    onClick={() => setPreviewImg(capturaPagoPreview)}
                                                />
                                            )}
                                        <span className="text-sm text-gray-700 truncate flex-1">{capturaPago.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => { setCapturaPago(null); setCapturaPagoPreview(null); }}
                                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-white transition-colors shrink-0"
                                            aria-label="Quitar captura"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <label className="flex items-center justify-center gap-2 w-full py-3.5 bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-colors">
                                        <ImagePlus size={18} className="text-gray-400" />
                                        <span className="text-sm text-gray-500">Subir captura o PDF</span>
                                        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => {
                                            const file = e.target.files[0] || null;
                                            setCapturaPago(file);
                                            if (!file) { setCapturaPagoPreview(null); return; }
                                            const reader = new FileReader();
                                            reader.onloadend = () => setCapturaPagoPreview(reader.result);
                                            reader.readAsDataURL(file);
                                        }} />
                                    </label>
                                )}
                            </div>
                            </div>

                            <div className="space-y-5">

                            {/* Lo que pidio el empleado, para que CxP vea de donde sale el anticipo */}
                            {(() => {
                                const { lineas, total } = desgloseAnticipo(pagoModal);
                                const dias = calcularNoches(pagoModal.fecha_inicio, pagoModal.fecha_fin) + 1;
                                const noCuadra = Math.abs(total - (pagoModal.monto_total || 0)) > 0.005;
                                return (
                                    <div className="rounded-2xl border border-gray-100 bg-gray-50/70 text-sm">
                                        <div className="px-4 py-2.5 border-b border-gray-100">
                                            <p className="font-semibold text-gray-900">Detalle del anticipo</p>
                                            {pagoModal.fecha_inicio && (
                                                <p className="text-xs text-gray-500 mt-0.5">
                                                    {fechaCorta(pagoModal.fecha_inicio)}
                                                    {pagoModal.fecha_fin && pagoModal.fecha_fin !== pagoModal.fecha_inicio && ` al ${fechaCorta(pagoModal.fecha_fin)}`}
                                                    {pagoModal.fecha_fin && ` · ${dias} ${dias === 1 ? 'dia' : 'dias'}`}
                                                    {pagoModal.hora_salida && ` · sale ${pagoModal.hora_salida}`}
                                                </p>
                                            )}
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {lineas.length === 0 && (
                                                <p className="px-4 py-2.5 text-gray-500">Sin gastos cargados.</p>
                                            )}
                                            {lineas.map(({ id, label, icon: Icon, formula, detalle, monto }) => (
                                                <div key={id} className="flex items-start justify-between gap-4 px-4 py-2">
                                                    <div className="flex items-start gap-2 min-w-0">
                                                        <Icon size={15} className="text-gray-400 shrink-0 mt-0.5" />
                                                        <div className="min-w-0">
                                                            <span className="text-gray-700">{label}</span>
                                                            {formula && <span className="text-gray-500"> · {formula}</span>}
                                                            {detalle && <p className="text-xs text-gray-500 break-words">{detalle}</p>}
                                                        </div>
                                                    </div>
                                                    <span className="font-medium text-gray-900 shrink-0">${monto.toFixed(2)}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-between gap-4 px-4 py-2.5 border-t border-gray-100">
                                            <span className="text-gray-700 font-medium">Total</span>
                                            <span className="font-bold text-gray-900">${total.toFixed(2)}</span>
                                        </div>
                                        {noCuadra && (
                                            <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 text-xs text-amber-700 flex items-start gap-1.5 rounded-b-2xl">
                                                <AlertCircle size={13} className="shrink-0 mt-0.5" />
                                                <span>El desglose no coincide con el anticipo aprobado (${pagoModal.monto_total?.toFixed(2)}).</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Proceso del calculo de la tasa */}
                            {proyeccionLoading && (
                                <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
                                    <Loader2 size={16} className="animate-spin" /> Consultando la tasa...
                                </div>
                            )}

                            {!proyeccionLoading && proyeccionError && (
                                <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-100 rounded-2xl text-sm text-red-700">
                                    <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                    <span>{proyeccionError}</span>
                                </div>
                            )}

                            {!proyeccionLoading && proyeccionTasa && (
                                <div className="rounded-2xl border border-blue-100 bg-blue-50/50 overflow-hidden">
                                    <div className="px-4 py-3 space-y-2 text-sm">
                                        <div className="flex items-center justify-between gap-4">
                                            <span className="text-gray-600">
                                                Tasa BCV del {fechaCorta(proyeccionTasa.fechaTasaBase)}
                                            </span>
                                            <span className="font-medium text-gray-900">{formatoTasa(proyeccionTasa.tasaBase)}</span>
                                        </div>
                                        <div className="flex items-center justify-between gap-4">
                                            <span className="text-gray-600">
                                                Variacion promedio (ultimos {proyeccionTasa.variacionesUsadas} dias)
                                            </span>
                                            <span className={`font-medium ${proyeccionTasa.promedioVariacion >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                {formatoPorcentaje(proyeccionTasa.promedioVariacion)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between gap-4 pt-2 border-t border-blue-100">
                                            <span className="text-gray-700 font-medium">Tasa de pago</span>
                                            <span className="font-bold text-blue-700 text-base">{formatoTasa(proyeccionTasa.tasaAplicada)}</span>
                                        </div>
                                    </div>

                                    {proyeccionTasa.usoFechaAnterior && (
                                        <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 text-xs text-amber-700 flex items-start gap-1.5">
                                            <AlertCircle size={13} className="shrink-0 mt-0.5" />
                                            <span>El {fechaCorta(proyeccionTasa.fechaPago)} no tiene tasa publicada; se usa la del {fechaCorta(proyeccionTasa.fechaTasaBase)}.</span>
                                        </div>
                                    )}
                                    {proyeccionTasa.stale && (
                                        <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 text-xs text-amber-700 flex items-start gap-1.5">
                                            <AlertCircle size={13} className="shrink-0 mt-0.5" />
                                            <span>Profit no respondio; se esta usando la ultima tasa consultada.</span>
                                        </div>
                                    )}
                                    {proyeccionTasa.variacionesUsadas < proyeccionTasa.variacionesEsperadas && (
                                        <div className="px-4 py-2 bg-amber-50 border-t border-amber-100 text-xs text-amber-700 flex items-start gap-1.5">
                                            <AlertCircle size={13} className="shrink-0 mt-0.5" />
                                            <span>Solo hay {proyeccionTasa.variacionesUsadas} de {proyeccionTasa.variacionesEsperadas} variaciones publicadas; el promedio sale de esas.</span>
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => setVerDetalleTasa(v => !v)}
                                        className="w-full px-4 py-2.5 border-t border-blue-100 text-xs font-medium text-blue-700 hover:bg-blue-50 transition-colors flex items-center justify-center gap-1.5"
                                    >
                                        {verDetalleTasa ? 'Ocultar el detalle' : 'Ver como se calculo'}
                                        <ChevronDown size={14} className={`transition-transform ${verDetalleTasa ? 'rotate-180' : ''}`} />
                                    </button>

                                    {verDetalleTasa && (
                                        <div className="border-t border-blue-100 bg-white/70 px-4 py-3">
                                            <p className="text-xs text-gray-500 mb-2">
                                                Se promedian las ultimas {proyeccionTasa.variacionesUsadas} variaciones hacia atras desde la fecha de pago,
                                                contando la de ese mismo dia.
                                                Cada dia se compara contra la tasa publicada anterior; los dias sin tasa
                                                (fines de semana y feriados) no cuentan.
                                            </p>
                                            <div className="space-y-1">
                                                {proyeccionTasa.variaciones.map(v => (
                                                    <div key={v.fecha} className="flex items-center justify-between gap-3 text-xs py-1 border-b border-gray-100 last:border-0">
                                                        <span className="text-gray-700 font-medium w-20 shrink-0">{fechaCorta(v.fecha)}</span>
                                                        <span className="text-gray-400 flex-1 truncate">
                                                            {formatoTasa(v.tasa)} vs {formatoTasa(v.tasaAnterior)} del {fechaCorta(v.fechaAnterior)}
                                                        </span>
                                                        <span className={`font-semibold shrink-0 ${v.variacion >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                            {formatoPorcentaje(v.variacion)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-100">
                                                Promedio {formatoPorcentaje(proyeccionTasa.promedioVariacion)} ·
                                                {' '}{formatoTasa(proyeccionTasa.tasaBase)} + ({formatoTasa(proyeccionTasa.tasaBase)} x {formatoPorcentaje(proyeccionTasa.promedioVariacion)}) =
                                                {' '}<span className="font-semibold text-gray-700">{formatoTasa(proyeccionTasa.tasaAplicada)}</span>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Monto resultante */}
                            {!proyeccionLoading && proyeccionTasa && (
                                <div className="rounded-2xl bg-gray-900 text-white px-5 py-4">
                                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Monto a pagar</p>
                                    <p className="text-2xl font-bold">Bs. {formatoBs(montoBsCalculado)}</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        ${pagoModal.monto_total?.toFixed(2)} x {formatoTasa(proyeccionTasa.tasaAplicada)}
                                    </p>
                                </div>
                            )}
                            </div>
                        </div>

                        <div className="flex gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100 shrink-0">
                            <button onClick={cerrarPagoModal}
                                className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors">
                                Cancelar
                            </button>
                            <button onClick={handlePagar}
                                disabled={!proyeccionTasa || proyeccionLoading || submitting}
                                className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                {submitting ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                                Confirmar pago
                            </button>
                        </div>
                    </motion.div>
                </div>
            ), document.body)}

            {/* Modal de Rechazo */}
            {showRechazoModal && createPortal((
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto p-4 flex items-start justify-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl my-auto"
                    >
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Rechazar Solicitud</h2>
                        <p className="text-gray-500 mb-6">
                            Solicitud {solicitudParaRechazar?.codigo} de {solicitudParaRechazar?.empleado_nombre}
                        </p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Motivo del rechazo</label>
                            <textarea
                                value={motivoRechazo}
                                onChange={e => setMotivoRechazo(e.target.value)}
                                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none text-base"
                                rows={4}
                                placeholder="Explica el motivo del rechazo para que el empleado pueda corregir..."
                                autoFocus
                            />
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => { setShowRechazoModal(false); setMotivoRechazo(''); setSolicitudParaRechazar(null); }}
                                className="flex-1 py-3 text-gray-600 font-medium hover:bg-gray-50 rounded-xl transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleRechazar}
                                disabled={!motivoRechazo.trim() || submitting}
                                className="flex-1 py-3 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {submitting ? <RefreshCw size={18} className="animate-spin" /> : <ThumbsDown size={18} />}
                                Rechazar
                            </button>
                        </div>
                    </motion.div>
                </div>
            ), document.body)}

            {/* Devolver la legalizacion al empleado (CxP) */}
            {devolverModal && createPortal((
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto p-4 flex items-start justify-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl my-auto"
                    >
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Devolver legalización</h2>
                        <p className="text-gray-500 mb-6">
                            {devolverModal.codigo} — {devolverModal.empleado_nombre}. Vuelve a Pagado para que el empleado corrija y reenvíe.
                        </p>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Motivo</label>
                        <textarea
                            value={motivoDevolucion}
                            onChange={e => setMotivoDevolucion(e.target.value)}
                            className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none resize-none text-base"
                            rows={4}
                            placeholder="Qué tiene que corregir el empleado. Se le notificará..."
                            autoFocus
                        />
                        {(devolverModal.reintegros || []).some(r => !r.anulado && r.sha256) && (
                            <div className="mt-5">
                                <p className="text-sm font-medium text-gray-700">Pagos del reintegro que no son válidos</p>
                                <p className="text-xs text-gray-500 mb-2">
                                    Marca solo los que no son válidos: esos dejan de contar y el comprobante no se podrá volver a subir. Los demás siguen contando para el reintegro.
                                </p>
                                <div className="space-y-2">
                                    {devolverModal.reintegros.filter(r => !r.anulado && r.sha256).map(r => (
                                        <label key={r.sha256} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={reintegrosAnular.includes(r.sha256)}
                                                onChange={e => setReintegrosAnular(prev => e.target.checked
                                                    ? [...prev, r.sha256]
                                                    : prev.filter(x => x !== r.sha256))}
                                                className="w-4 h-4 accent-amber-600"
                                            />
                                            <span className="text-sm text-gray-800">
                                                Bs. {formatoBs(r.monto_bs || 0)}
                                                <span className="text-gray-500">
                                                    {r.fecha ? ` · ${new Date(r.fecha).toLocaleDateString('es-VE')}` : ''}
                                                    {r.referencia ? ` · Ref. ${r.referencia}` : ''}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => { setDevolverModal(null); setMotivoDevolucion(''); }}
                                className="flex-1 py-3 text-gray-600 font-medium hover:bg-gray-50 rounded-xl transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDevolverLegalizacion}
                                disabled={!motivoDevolucion.trim() || submitting}
                                className="flex-1 py-3 bg-amber-600 text-white font-medium rounded-xl hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {submitting ? <RefreshCw size={18} className="animate-spin" /> : <RefreshCw size={18} />}
                                Devolver
                            </button>
                        </div>
                    </motion.div>
                </div>
            ), document.body)}

            {/* Modal de Anulación (CxP) */}
            {anularModal && createPortal((
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto p-4 flex items-start justify-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl my-auto"
                    >
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Anular Solicitud</h2>
                        <p className="text-gray-500 mb-6">
                            {anularModal.codigo} — {anularModal.empleado_nombre}
                        </p>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Motivo de la anulación</label>
                            <textarea
                                value={motivoAnulacion}
                                onChange={e => setMotivoAnulacion(e.target.value)}
                                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none text-base"
                                rows={4}
                                placeholder="Explica por qué se anula esta solicitud. El empleado será notificado..."
                                autoFocus
                            />
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => { setAnularModal(null); setMotivoAnulacion(''); }}
                                className="flex-1 py-3 text-gray-600 font-medium hover:bg-gray-50 rounded-xl transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleAnular}
                                disabled={!motivoAnulacion.trim() || submitting}
                                className="flex-1 py-3 bg-red-500 text-white font-medium rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {submitting ? <RefreshCw size={18} className="animate-spin" /> : <Ban size={18} />}
                                Anular
                            </button>
                        </div>
                    </motion.div>
                </div>
            ), document.body)}

            {/* Aprobar por CxP en lugar del supervisor */}
            {aprobarCxpModal && createPortal((
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto p-4 flex items-start justify-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl my-auto"
                    >
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Aprobar por CxP</h2>
                        <p className="text-gray-500 mb-5">
                            {aprobarCxpModal.codigo} — {aprobarCxpModal.empleado_nombre} · ${aprobarCxpModal.monto_total?.toFixed(2)}
                        </p>
                        <div className="flex gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800 mb-5">
                            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                            <p>
                                Esto aprueba la solicitud en lugar de <strong>{aprobarCxpModal.aprobador_nombre || 'el supervisor'}</strong>.
                                Úsalo solo si no puede aprobarla (vacaciones, reposo, etc.). Quedará registrado que aprobó CxP y el motivo; el empleado y el supervisor serán notificados.
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Por qué aprueba CxP</label>
                            <textarea
                                value={motivoAprobacionCxp}
                                onChange={e => setMotivoAprobacionCxp(e.target.value)}
                                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none resize-none text-base"
                                rows={3}
                                placeholder="Ej.: el supervisor está de vacaciones hasta el 20 y el viaje sale el 15"
                                autoFocus
                            />
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => { setAprobarCxpModal(null); setMotivoAprobacionCxp(''); }}
                                className="flex-1 py-3 text-gray-600 font-medium hover:bg-gray-50 rounded-xl transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleAprobarCxp}
                                disabled={!motivoAprobacionCxp.trim() || submitting}
                                className="flex-1 py-3 bg-amber-600 text-white font-medium rounded-xl hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {submitting ? <RefreshCw size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
                                Aprobar
                            </button>
                        </div>
                    </motion.div>
                </div>
            ), document.body)}

            {/* Panel de Tarjetas Gerenciales (CxP/Admin) */}
            {showTarjetasPanel && createPortal((
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto p-4 flex items-start justify-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl max-h-[85vh] flex flex-col my-auto"
                    >
                        <div className="flex items-center justify-between mb-1">
                            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <CreditCard size={20} style={{ color: '#c8991f' }} /> Tarjetas gerenciales
                            </h2>
                            <button
                                onClick={() => { setShowTarjetasPanel(false); setTarjetaBusqueda(''); setTarjetaResultados([]); }}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>
                        <p className="text-gray-500 text-sm mb-5">
                            Empleados que pueden registrar gastos con tarjeta gerencial (sin anticipo ni pago de CxP).
                        </p>

                        <div className="relative mb-4">
                            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={tarjetaBusqueda}
                                onChange={e => setTarjetaBusqueda(e.target.value)}
                                placeholder="Buscar empleado por nombre o ficha para agregar..."
                                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e6cf7a] focus:border-[#e6cf7a] outline-none text-base"
                            />
                        </div>

                        <div className="overflow-y-auto flex-1 -mx-2 px-2">
                            {tarjetaBusqueda.trim().length >= 2 ? (
                                tarjetaResultados.length === 0 ? (
                                    <p className="text-center text-gray-400 py-6 text-sm">Sin resultados</p>
                                ) : (
                                    <div className="space-y-2">
                                        {tarjetaResultados.map(e => (
                                            <div key={e.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                                                <div>
                                                    <p className="font-medium text-gray-900 text-sm">{e.nombre}</p>
                                                    <p className="text-xs text-gray-500">{[e.ficha, e.cargo].filter(Boolean).join(' · ')}</p>
                                                </div>
                                                {e.tiene_tarjeta_gerencial ? (
                                                    <button onClick={() => toggleTarjeta(e.id, false)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"><X size={14} /> Quitar</button>
                                                ) : (
                                                    <button onClick={() => toggleTarjeta(e.id, true)} className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-lg transition-colors text-sm font-medium" style={{ backgroundColor: '#c8991f' }}><Plus size={14} /> Habilitar</button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )
                            ) : (
                                <>
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Con tarjeta habilitada ({tarjetasEmpleados.length})</h4>
                                    {tarjetasLoading ? (
                                        <div className="text-center py-6"><Loader2 size={24} className="animate-spin text-gray-400 mx-auto" /></div>
                                    ) : tarjetasEmpleados.length === 0 ? (
                                        <p className="text-center text-gray-400 py-6 text-sm">Nadie tiene tarjeta habilitada todavía. Búscalo arriba para agregarlo.</p>
                                    ) : (
                                        <div className="space-y-2">
                                            {tarjetasEmpleados.map(e => (
                                                <div key={e.id} className="flex items-center justify-between p-3 bg-[#fdf6e3] border border-[#e6cf7a] rounded-xl">
                                                    <div>
                                                        <p className="font-medium text-gray-900 text-sm">{e.nombre}</p>
                                                        <p className="text-xs text-gray-500">{[e.ficha, e.cargo].filter(Boolean).join(' · ')}</p>
                                                    </div>
                                                    <button onClick={() => toggleTarjeta(e.id, false)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-red-500 border border-red-200 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"><X size={14} /> Quitar</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>
                </div>
            ), document.body)}
        </div>
    );
};

export default ViaticosView;
